import React, { useState, useEffect, useRef, useMemo } from 'react';
import Icon from '../shared/Icon';
import Timestamp from '../shared/Timestamp';
import { formatTimestamp } from '../../../shared/datetime/timestamps';
import Chip from '../shared/Chip';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './AskResAI.css';
import { apiService } from '../../../services/api';
import SchemaApprovalModal from './SchemaApprovalModal';
import AvailableDatasets from './AvailableDatasets';
import HistoryPanel from './iteration1/HistoryPanel';
import ThreadHeader from './iteration1/ThreadHeader';
import ProgressRow from './iteration1/ProgressRow';
import Modal from './iteration1/Modal';
import TooltipLayer from '../shared/Tooltip';
import { ShareModal, StopSharingModal } from './iteration1/ShareModals';
import SharedFailedAnswer from './iteration1/SharedFailedAnswer';
import {
  PersonAvatar,
  SharedLine,
  ShareEventDivider,
  ForChip,
  DisabledAction,
  WithheldCard,
  AudienceNote,
  NewActivityButton,
  WITHHELD_ANSWER_TEXT,
  READ_ONLY_TEXT,
} from './iteration1/SharedThreadParts';
import { FailedAnswer, StoppedLine } from './iteration1/AnswerOutcome';
import { datasetChange, datasetChangeIcon, datasetChangeText, sharedDatasetChange } from './iteration1/datasetChanges';
import useAnnouncer from './iteration1/useAnnouncer';
import './iteration1/Iteration1.css';
import ExtractionTable, { parseExtractionResults } from './ExtractionTable';
import { makeMarkdownTableComponents } from './MarkdownTableAdapter';
import ModuleLibraryFooter from './ModuleLibraryFooter';
import StructuredResponsePanel from './StructuredResponsePanel';
import OutputFormatSelect from './OutputFormatSelect';
import OutputViewResult, { outputViewResultToText } from './OutputViewResult';
import { useLocation } from 'react-router-dom';
import QuickUploadModal from '../QuickUploadModal/QuickUploadModal';
import { initialsOf, initialsStyle, resolveAvatarKind } from '../Account/avatar';
import VoiceRecorderWidget from '../VoiceRecorder/VoiceRecorderWidget';
import { useConfirm } from '../shared/useConfirm';
import ReviewRequiredBanner from '../shared/ReviewRequiredBanner';
import TranscriptViewerModal from '../VoiceRecorder/TranscriptViewerModal';
import DrilldownChartPanel from './DrilldownChartPanel';
import ThreadRowMenu from './ThreadRowMenu';
import SharePopover from './SharePopover';
import SharedWithMeList from './SharedWithMeList';
import LockedQueryNotice from './LockedQueryNotice';
import SharedConversationHeader from './SharedConversationHeader';
import SharingEndedNotice from './SharingEndedNotice';
import {
  VISIBILITY_ORGANIZATION,
  VISIBILITY_PRIVATE,
  isThreadOwner,
  isThreadShared,
  countOtherAskers,
  stopSharingMessage,
  deleteSharedMessage,
  deleteSharedQueryMessage,
  SHARE_ERROR,
  confirmTitle,
  answeredForLabel,
  isWithheld,
  askersFrom,
  WITHHELD_ANSWER,
  SHARED_COMPOSER_PLACEHOLDER,
  COMPOSER_PLACEHOLDER,
  SHARED_AUDIENCE,
  READ_ONLY_ROLE,
  TURNS_POLL_MS,
  maxTurnSeq,
  withShareEvents,
  lostAccessReason,
  QUERY_GONE,
  LOST_ACCESS_ANNOUNCEMENT,
  datasetsChangedBefore,
  datasetsChangedLine,
} from './sharedQueries';
import { useFlag } from '@unleash/proxy-client-react';
import {
  TIMEOUT_NOTICE,
  SEND_FAILED_NOTICE,
  AGENT_ERROR_NOTICE,
  STILL_ANSWERING_HINT,
  THREAD_BUSY_NOTICE,
  THREAD_BUSY_RETRY_NOTICE,
  isThreadBusy,
  isBusyNotice,
  turnFailureNotice,
  stateFailureNotice,
} from '../../../utils/chatTurnNotice';

// When a message was written, as the server stored it with the message. A message saved
// before the server kept times has none and shows the formatter's absence value: a time
// this browser happened to see it would differ between devices and vanish with site data.
const sentAt = (msg) => msg?.additional_kwargs?.sent_at;

// Where this browser used to keep the times it stamped itself. Nothing reads it now; it
// is cleared once so the stale times do not linger in storage.
const LEGACY_MESSAGE_TIMES_KEY = 'resai_message_times';

/** Strip download/sandbox links from display. Download remains via the DOWNLOAD PDF button. */
function hideDownloadLinkInText(content) {
  if (typeof content !== 'string') return content;
  // Remove markdown links whose URL starts with "sandbox:" (blob paths the agent includes)
  let cleaned = content.replace(/\[([^\]]*)\]\(sandbox:[^)]*\)/gi, '');
  // Remove markdown links whose URL starts with "resai/" (internal blob paths)
  cleaned = cleaned.replace(/\[([^\]]*)\]\(resai\/[^)]*\)/gi, '');
  // Remove any markdown link containing "download" in its text (various phrasings)
  cleaned = cleaned.replace(/\[([^\]]*[Dd]ownload[^\]]*)\]\([^)]*\)/g, '');
  // Remove leftover "using the link below:" or "using the following link:" with nothing after
  cleaned = cleaned.replace(/using the (?:link (?:below|provided above|above)|following link):?\s*\.?\s*/gi, '.');
  // Remove leftover blank lines
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n');
  return cleaned;
}

/**
 * Ask ResAI Component - LangGraph-native chat interface
 *
 * Uses LangGraph message types directly:
 * - human: User messages
 * - ai: Assistant messages (may contain tool_calls)
 * - tool: Tool execution results
 *
 * @param {Object} props
 * @param {Function} [props.onScheduleReport] - Called when user chooses "Schedule as report" with ({ queryText, responsePreview, threadId })
 */
const AskResAI = ({
  user,
  onScheduleReport,
  onShowNotification,
  enableCsvHitlReview,
  submittedReviewJobs,
  onOpenReview,
  datasetsRefreshKey,
  onPublishSuccess,
  features,
}) => {
  const structuredResponseEnabled = useFlag('enable_structured_response');
  // Gates the report email's "View in Workbench" → open the report's chat thread.
  const reportWorkbenchChatEnabled = useFlag('report_workbench_chat');
  // PRD-1337: drill-down deep-link into the widget-bound conversation.
  const drilldownEnabled =
    useFlag('enable_insights_drilldown') || process.env.REACT_APP_FORCE_INSIGHTS_DRILLDOWN === 'true';
  const quickUploadEnabled = useFlag('quick_upload_chat_input');
  const moduleFrameworkEnabled = useFlag('enable_module_framework');
  const chatMessageEditEnabled = useFlag('chat_message_edit');
  // PRD-1579: per-tenant sharing comes from the server's profile features, not
  // a browser-side Unleash evaluation (which has no org context). Off until the
  // profile has loaded.
  const sharedQueriesEnabled = features?.shared_queries_enabled === true;
  // PRD-1614: Ask ResAI Iteration 1 — two panels, pins, progress and Stop. Off = today's screen.
  const iter1 = useFlag('ask_resai_iteration1');
  const [sideTab, setSideTab] = useState('history');
  // When the answer being prepared started: the send, or the run's start on returning (PRD-1617).
  const [runStartedAt, setRunStartedAt] = useState(null);
  // The answer in progress is this viewer's own, so Stop applies (PRD-1619: asker only).
  const [ownRun, setOwnRun] = useState(false);
  // A failed turn shown as an error state with Try again (PRD-1620/1624), not as a banner.
  const [turnOutcome, setTurnOutcome] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [shareTarget, setShareTarget] = useState(null);
  const [stopShareTarget, setStopShareTarget] = useState(null);
  const [announce, announceRegion] = useAnnouncer();
  const deleteCancelRef = useRef(null);
  const iter1RootRef = useRef(null);

  // In-chat voice recording (PRD-941): gated by the Unleash kill-switch AND the
  // server-enforced RBAC capability. Both must be true for the entry point to appear.
  const voiceCaptureFlagEnabled = useFlag('voice_capture');
  const canRecordAudioDataset = !!(features && features.can_record_audio_dataset);
  const voiceCaptureEnabled = voiceCaptureFlagEnabled && canRecordAudioDataset;

  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  const [transcriptDataset, setTranscriptDataset] = useState(null);
  const [retryingDatasetIds, setRetryingDatasetIds] = useState(() => new Set());

  const handleRecordClick = () => {
    if (!voiceCaptureEnabled) return;
    setShowVoiceRecorder(true);
  };

  const handleVoiceRecorderClose = () => setShowVoiceRecorder(false);

  const handleVoiceSubmitted = () => {
    // New voice dataset is processing — refresh the sidebar so it shows + polls to ready.
    if (onPublishSuccess) onPublishSuccess();
  };

  const handleViewTranscript = (dataset) => {
    setTranscriptDataset(dataset);
  };

  const handleRetryDataset = async (dataset) => {
    if (!dataset?.id || retryingDatasetIds.has(dataset.id)) return;
    setRetryingDatasetIds((prev) => new Set(prev).add(dataset.id));
    try {
      await apiService.reprocessDataset(dataset.id);
      if (onShowNotification) onShowNotification('Reprocessing started.', 'success');
      if (onPublishSuccess) onPublishSuccess();
    } catch (error) {
      // 409 — the job already moved on (double-tap or it recovered). Not an error to the user.
      if (error.status === 409) {
        if (onShowNotification) onShowNotification('This recording is already processing.', 'info');
        if (onPublishSuccess) onPublishSuccess();
      } else {
        const msg = error?.responseData?.detail || error?.message || 'Failed to reprocess recording.';
        if (onShowNotification) onShowNotification(typeof msg === 'string' ? msg : 'Failed to reprocess recording.', 'error');
      }
    } finally {
      setRetryingDatasetIds((prev) => {
        const next = new Set(prev);
        next.delete(dataset.id);
        return next;
      });
    }
  };

  // Quick-upload (paperclip) flow: opens a 2-step "New dataset" modal
  const quickUploadInputRef = useRef(null);
  const [showQuickUpload, setShowQuickUpload] = useState(false);
  const [quickUploadFiles, setQuickUploadFiles] = useState([]);

  const handleQuickUploadClick = () => {
    if (!quickUploadEnabled) return;
    if (quickUploadInputRef.current) quickUploadInputRef.current.click();
  };

  const handleQuickUploadFilesChosen = (e) => {
    if (!quickUploadEnabled) return;
    const picked = e.target.files ? Array.from(e.target.files) : [];
    if (picked.length === 0) return;
    setQuickUploadFiles(picked);
    setShowQuickUpload(true);
    e.target.value = '';
  };

  const handleQuickUploadClose = () => {
    setShowQuickUpload(false);
    setQuickUploadFiles([]);
  };

  const handleQuickUploadPublishSuccess = () => {
    if (onPublishSuccess) onPublishSuccess();
  };

  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const dragCounterRef = useRef(0);

  const handleDragEnter = (e) => {
    if (!quickUploadEnabled) return;
    if (!e.dataTransfer?.types?.includes('Files')) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    setIsDraggingFile(true);
  };

  const handleDragOver = (e) => {
    if (!quickUploadEnabled) return;
    if (!e.dataTransfer?.types?.includes('Files')) return;
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    if (!quickUploadEnabled) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) setIsDraggingFile(false);
  };

  const handleDrop = (e) => {
    if (!quickUploadEnabled) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDraggingFile(false);
    const dropped = e.dataTransfer?.files ? Array.from(e.dataTransfer.files) : [];
    if (dropped.length === 0) return;
    setQuickUploadFiles(dropped);
    setShowQuickUpload(true);
  };

  // Core state
  const [query, setQuery] = useState('');
  const [threads, setThreads] = useState([]);
  const [confirmDelete, confirmDeleteDialog] = useConfirm();
  // PRD-1571: the Share popover's row ({ thread, anchorEl }) while it is open.
  const [sharePopover, setSharePopover] = useState(null);
  // PRD-1571 R6: other people's queries shared with the organisation.
  const [sharedThreads, setSharedThreads] = useState([]);
  const [sharedExpanded, setSharedExpanded] = useState(true);
  // PRD-1571 R7/R8: who asked each turn of the open shared conversation.
  const [turns, setTurns] = useState([]);
  // PRD-1571 R13: the newest turn this view has rendered, so the live-turns
  // poll can tell when someone else has asked.
  const lastTurnSeqRef = useRef(0);
  // PRD-1571 R4: the open shared query stopped being readable to this participant.
  const [sharingEnded, setSharingEnded] = useState(false);
  const [goneReason, setGoneReason] = useState(null);
  const threadRowRefs = useRef({});
  const [activeThread, setActiveThread] = useState(null);
  const location = useLocation();
  const [messages, setMessages] = useState([]);  // LangGraph native format
  useEffect(() => {
    try {
      localStorage.removeItem(LEGACY_MESSAGE_TIMES_KEY);
    } catch {
      /* storage unavailable: nothing to clear */
    }
  }, []);
  const [loading, setLoading] = useState(false);
  const [loadingThreads, setLoadingThreads] = useState(true);
  // True while an existing thread's state is being fetched, so the chat area
  // shows a loading indicator instead of a blank pane during the round-trip.
  const [threadLoading, setThreadLoading] = useState(false);
  const [error, setError] = useState(null);
  // PRD-09: the user's avatar photo/logo (server-proxied object URL) for chat message
  // rows — so the profile picture shows everywhere, not just the header. Null = initials.
  const [userAvatarUrl, setUserAvatarUrl] = useState(null);
  const userAvatarKind = resolveAvatarKind(user?.profile, !!user?.org_logo_url);
  const userAvatarColor = user?.profile?.avatar_color || '';

  // Streaming state
  const [streamingContent, setStreamingContent] = useState('');
  const [streamingToolCalls, setStreamingToolCalls] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isAwaitingResponse, setIsAwaitingResponse] = useState(false);
  // An answer is on its way, live or by poll. Nothing else can be sent until it lands:
  // a second question used to vanish without a word while Send looked live.
  const answerInFlight = loading || isStreaming || isAwaitingResponse;

  // UI state
  const [hideToolMessages, setHideToolMessages] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [datasetPanelOpen, setDatasetPanelOpen] = useState(true);
  const [selectedDatasets, setSelectedDatasets] = useState([]);
  // PRD-1359: every dataset the sidebar has loaded, refreshed on each of its
  // polls. `selectedDatasets` is not enough — a historical turn names datasets
  // the user may since have deselected, and its banner still has to be right.
  const [loadedDatasets, setLoadedDatasets] = useState([]);
  // PRD-977: dataset IDs the active thread was last grounded with. Populated
  // from /threads/{id}/state on thread switch so the chip panel can re-hydrate
  // the selection — the perceived state ("chip is selected") was diverging
  // from the actual state (no dataset_ids sent on send) when a user reopened
  // an existing thread and fired a follow-up.
  const [threadDatasetIds, setThreadDatasetIds] = useState([]);
  // Sticky-per-user dataset IDs from the SOP Library footer. Merged into
  // `datasetIds` at every send/regenerate call site so module-owned SOPs
  // ground the same queries as working datasets.
  const [moduleSelectedDatasetIds, setModuleSelectedDatasetIds] = useState([]);
  const [savedQueriesExpanded, setSavedQueriesExpanded] = useState(true);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Edit state
  const [editingMessageIndex, setEditingMessageIndex] = useState(null);
  const [editContent, setEditContent] = useState('');
  // Thread rename state (PRD-1075)
  const [renamingThreadId, setRenamingThreadId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  // Human-in-the-loop state
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(null);

  // Checkpoint tracking for edit/regenerate
  const [checkpoints, setCheckpoints] = useState([]);

  // Structured responses keyed by AI message originalIndex
  const [structuredResponses, setStructuredResponses] = useState({});

  // Output-format picker: the formats available for the current dataset
  // selection, the user's current choice (null = "None / plain answer"), and
  // the filled results keyed by AI message originalIndex.
  const [availableFormats, setAvailableFormats] = useState([]);
  const [selectedOutputFormat, setSelectedOutputFormat] = useState(null);
  const [outputViewResults, setOutputViewResults] = useState({});

  // Refresh the available output formats whenever the effective dataset set
  // (working + module-owned) changes. The read path is gated server-side, so
  // when the feature is off the endpoint returns an empty list and the picker
  // stays hidden — the FE needs no flag of its own. If the current choice is no
  // longer offered, fall back to "None" so we never send a stale format.
  useEffect(() => {
    const datasetIds = Array.from(new Set([
      ...selectedDatasets.map(d => d.id),
      ...moduleSelectedDatasetIds,
    ]));
    let cancelled = false;
    (async () => {
      try {
        const { formats } = await apiService.getOutputFormats(datasetIds);
        if (cancelled) return;
        const list = Array.isArray(formats) ? formats : [];
        setAvailableFormats(list);
        setSelectedOutputFormat(prev => (prev && list.includes(prev) ? prev : null));
      } catch (err) {
        // Non-critical enrichment — fail silent (just hide the picker) so a
        // not-yet-deployed endpoint or a transient error never disrupts chat.
        if (cancelled) return;
        console.warn('[AskResAI] Could not load output formats:', err);
        setAvailableFormats([]);
        setSelectedOutputFormat(null);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDatasets, moduleSelectedDatasetIds]);

  // Which AI message has the 3-dot actions menu open (originalIndex or null)
  const [openActionsMenuIndex, setOpenActionsMenuIndex] = useState(null);

  // Reasoning/evidence panel visibility per message (toggled from 3-dot menu)
  // Uses Sets so multiple messages can have panels open simultaneously
  const [showReasoningForMsgs, setShowReasoningForMsgs] = useState(new Set());
  const [showEvidenceForMsgs, setShowEvidenceForMsgs] = useState(new Set());
  const [showExtractionEvidenceForMsgs, setShowExtractionEvidenceForMsgs] = useState(new Set());

  const messagesEndRef = useRef(null);
  // Shared Queries handoff (R13): other people's turns arrive while you read. At the end of the
  // thread it scrolls to show them; scrolled up, nothing moves and New activity appears. Asking
  // or stopping counts as being at the end, so your own question and answer always come into view.
  const nearEndRef = useRef(true);
  const sharedLiveRef = useRef(false);
  // Whether the open thread's newest turn is known yet; until it is, a turns poll only records it.
  const turnsPrimedRef = useRef(false);
  // The shared query the selector was set up for on opening, and the selection it started with
  // ({ threadId, ids }); only a change from that is the owner's to re-pin.
  const seededPinsRef = useRef(null);
  const answeredRef = useRef({ threadId: null, primed: false, ids: new Set() });
  const [newActivity, setNewActivity] = useState(false);
  // Query Thread Header: shadow-xl once the thread scrolls (Shared Queries handoff 2).
  const [threadScrolled, setThreadScrolled] = useState(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  // The last question sent (and its attachments), so a "thread busy" refusal can put
  // it back in the box.
  const lastSentRef = useRef(null);
  const startingSendRef = useRef(false);
  const abortStreamRef = useRef(null);
  // Skip loadThreadState when we just created a thread (prevents clearing optimistic messages)
  const skipLoadThreadStateRef = useRef(false);
  // Tracks the currently-active thread id so a slow loadThreadState response
  // can be discarded if the user has already switched away — otherwise the
  // late response paints the wrong chat's messages on screen.
  const activeThreadIdRef = useRef(null);
  // PRD-1337: open a ?drilldown_thread= at most once per mount (review on #182).
  const drilldownOpenedRef = useRef(false);
  const pendingResponsePollRef = useRef(null);
  const isStreamingRef = useRef(false);
  // This viewer's own answer is on its way (asked, edited or regenerated here). Set when they
  // send; cleared when nothing is in flight any more, when they stop, or on another thread.
  // Not `ownRun`, which the response poll also sets for anyone's run.
  const myAnswerRef = useRef(false);

  // Scroll to bottom when messages change
  useEffect(() => {
    // Your own answer, streaming or polled, keeps following the end.
    if (sharedLiveRef.current && !nearEndRef.current && !isStreamingRef.current && !myAnswerRef.current) {
      setNewActivity(true);
      return;
    }
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingContent]);

  useEffect(() => {
    if (!answerInFlight) myAnswerRef.current = false;
  }, [answerInFlight]);

  const followMyAnswer = () => {
    holdAtEnd();
    myAnswerRef.current = true;
  };

  const holdAtEnd = () => {
    nearEndRef.current = true;
    setNewActivity(false);
  };

  const handleMessagesScroll = (e) => {
    const el = e.currentTarget;
    nearEndRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
    if (nearEndRef.current) setNewActivity(false);
    setThreadScrolled(el.scrollTop > 0);
  };

  const jumpToNewActivity = () => {
    setNewActivity(false);
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    messagesEndRef.current?.scrollIntoView({ behavior: still ? 'auto' : 'smooth' });
    textareaRef.current?.focus();
  };

  // PRD-09: fetch the user's avatar (photo or org logo) once for chat message rows,
  // mirroring the header. Served proxied → fetched as an object URL (an <img> can't carry
  // the auth header). Null when the source is colour → message rows show initials.
  useEffect(() => {
    let cancelled = false;
    let created = null;
    const fetcher =
      userAvatarKind === 'photo'
        ? apiService.fetchProfilePhotoObjectUrl
        : userAvatarKind === 'logo'
        ? apiService.fetchOrgLogoObjectUrl
        : null;
    if (fetcher) {
      fetcher().then((u) => {
        if (cancelled) {
          if (u) URL.revokeObjectURL(u);
          return;
        }
        created = u;
        setUserAvatarUrl(u);
      });
    } else {
      setUserAvatarUrl(null);
    }
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [userAvatarKind, user]);

  // Close 3-dot menu when clicking outside
  useEffect(() => {
    if (openActionsMenuIndex === null) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest('.message-actions-dropdown')) {
        setOpenActionsMenuIndex(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openActionsMenuIndex]);

  // Load threads on mount
  useEffect(() => {
    loadThreads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deep-link: open a specific conversation from ?thread=<id>. This is how the
  // report-delivery email's "View in Workbench" button lands the recipient in
  // the report's chat (prompt + answer already present) so they can ask
  // follow-ups. Fetch the thread, make it active (the effect below loads its
  // state), then strip the param so a refresh or "New chat" doesn't reopen it.
  useEffect(() => {
    // The ?thread= deep-link is the report "View in Workbench" flow only. PRD-1337
    // drill-down uses its own ?drilldown_thread= param (handled below), so this stays
    // gated on report_workbench_chat alone — widening it to drilldownEnabled would open
    // this gated deep-link for an org that only has drill-down on (review on #182).
    if (!reportWorkbenchChatEnabled) return;
    const params = new URLSearchParams(window.location.search);
    const deepLinkThreadId = params.get('thread');
    if (!deepLinkThreadId) return;

    const stripThreadParam = () => {
      const url = new URL(window.location.href);
      url.searchParams.delete('thread');
      window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    };

    // Validate the param to a UUID before it ever reaches a request URL. `?thread=`
    // is attacker-controllable, and getThread interpolates it into the request path;
    // constraining it to a strict UUID stops a crafted value from shaping that path
    // (client-side request forgery — mirrors the backend's own UUID guard). A
    // non-UUID value is discarded, not fetched.
    const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!UUID_RE.test(deepLinkThreadId)) {
      stripThreadParam();
      onShowNotification?.('Could not open that conversation.', 'error');
      return;
    }

    let cancelled = false;
    apiService.getThread(deepLinkThreadId)
      .then((thread) => {
        if (cancelled || !thread) return;
        setActiveThread(thread);
        // Strip ?thread= only AFTER a successful open, so a transient failure can be
        // retried by refreshing rather than forcing the user back to the email
        // (PR #167 review §2).
        stripThreadParam();
      })
      .catch((err) => {
        console.error('[AskResAI] deep-link getThread failed:', err);
        onShowNotification?.('Could not open that conversation.', 'error');
      });
    return () => { cancelled = true; };
    // One-shot deep-link open keyed on the flag resolving; onShowNotification is a stable prop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportWorkbenchChatEnabled]);

  // Load messages when active thread changes
  useEffect(() => {
    activeThreadIdRef.current = activeThread?.id || null;
    // Another thread clears the header's shadow: one too short to scroll never fires scroll to.
    setThreadScrolled(false);
    setTurns([]);
    lastTurnSeqRef.current = 0;
    turnsPrimedRef.current = false;
    setSharingEnded(false);
    setGoneReason(null);
    if (activeThread) {
      if (skipLoadThreadStateRef.current) {
        skipLoadThreadStateRef.current = false;
        return;
      }
      // Clear the previous thread first. The sidebar does this itself, but the
      // link opens (report "View in Workbench", widget drill-down) only set the thread,
      // and the previous thread's messages then read as this one's while it loaded.
      setMessages([]);
      setCheckpoints([]);
      if (sharedQueriesEnabled && activeThread.locked) return;
      loadThreadState(activeThread.id);
    } else {
      setMessages([]);
      setCheckpoints([]);
    }
  }, [activeThread?.id]);

  // PRD-1337: open the drill-down thread named by ?drilldown_thread= exactly once, then
  // strip the param. Opening once (guarded by a ref) means a flag that resolves AFTER the
  // user has already started another chat cannot yank them back to the widget thread; and
  // stripping the param means a later refresh keeps them on whatever thread they moved to
  // rather than evicting it (review on #182). Distinct param name from the report
  // ?thread= deep-link so the two never collide.
  useEffect(() => {
    if (!drilldownEnabled) return undefined;
    const tid = new URLSearchParams(location.search).get('drilldown_thread');
    if (!tid) return undefined;
    if (drilldownOpenedRef.current) return undefined;
    const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!UUID_RE.test(tid)) return undefined;

    const stripDrilldownParam = () => {
      const url = new URL(window.location.href);
      url.searchParams.delete('drilldown_thread');
      window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    };

    // PRD-1467: only bail when we're ALREADY on the bound thread — a deliberate
    // drill-down click must win over whatever thread happens to be active (the old
    // "any active thread" bail silently dropped the param, so the widget thread
    // never opened and only loaded from Saved queries).
    if (activeThreadIdRef.current === tid) {
      drilldownOpenedRef.current = true;
      stripDrilldownParam();
      return undefined;
    }

    let cancelled = false;
    apiService.getThread(tid)
      .then((thread) => {
        if (cancelled || !thread) return;
        // PRD-1467: latch only once the open actually succeeds. Latching before the
        // await meant a transient empty/failed fetch (e.g. a just-created thread not
        // yet readable) permanently blocked the open — the drill-down came up blank.
        drilldownOpenedRef.current = true;
        setActiveThread(thread);
        stripDrilldownParam();
      })
      .catch((err) => {
        console.error('[AskResAI] drill-down open failed:', err);
        onShowNotification?.('Could not open that conversation.', 'error');
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, drilldownEnabled]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + 'px';
    }
  }, [query]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (!isStreaming) return;
      const threadId = activeThreadIdRef.current;
      if (!threadId) return;
      if (abortStreamRef.current) {
        abortStreamRef.current();
        abortStreamRef.current = null;
      }
      setStreamingContent('');
      setStreamingToolCalls([]);
      setIsStreaming(false);
      setLoading(false);
      loadThreadState(threadId);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [isStreaming]);

  useEffect(() => {
    isStreamingRef.current = isStreaming;
  }, [isStreaming]);
  useEffect(() => {
    return () => cancelPendingResponsePoll();
  }, []);

  const isUnansweredQuery = (msgs) => {
    if (!Array.isArray(msgs) || msgs.length === 0) return false;
    const last = msgs[msgs.length - 1];
    // PRD-1619: a stopped question ends there; nothing more is coming.
    if (last?.type === 'human' && last.additional_kwargs?.stopped) return false;
    if (last?.type === 'human') return true;
    if (last?.type === 'ai' && Array.isArray(last.tool_calls) && last.tool_calls.length > 0) return true;
    return false;
  };

  const cancelPendingResponsePoll = () => {
    if (pendingResponsePollRef.current) {
      clearTimeout(pendingResponsePollRef.current);
      pendingResponsePollRef.current = null;
    }
    setIsAwaitingResponse(false);
  };

  // The busy notice is only true until the earlier answer lands. Any other
  // banner is left alone.
  const clearBusyNotice = () => setError((current) => (isBusyNotice(current) ? null : current));

  // Front-loaded backoff caps the visible tail near 1s while keeping total
  // polling under ~60s if a server-side run truly never lands.
  const POLL_SCHEDULE_MS = [1000, 1500, 2000];
  const POLL_STEADY_MS = 3000;
  const POLL_MAX_ATTEMPTS = 22;
  // PRD-1624: an answer can run up to the 8-minute limit; ~8.5 min at 3s.
  const POLL_MAX_ATTEMPTS_ITER1 = 170;
  const _pollDelay = (attempt) => POLL_SCHEDULE_MS[attempt] ?? POLL_STEADY_MS;
  const pollForPendingResponse = (threadId, attempts = 0, answered = false) => {
    // Stop any prior timer but preserve isAwaitingResponse across ticks —
    // cancelPendingResponsePoll() also clears the indicator, which we don't
    // want until polling actually settles or bails out.
    if (pendingResponsePollRef.current) {
      clearTimeout(pendingResponsePollRef.current);
      pendingResponsePollRef.current = null;
    }
    setIsAwaitingResponse(true);
    pendingResponsePollRef.current = setTimeout(async () => {
      pendingResponsePollRef.current = null;
      if (activeThreadIdRef.current !== threadId) {
        setIsAwaitingResponse(false);
        return;
      }
      if (isStreamingRef.current) {
        setIsAwaitingResponse(false);
        return;
      }
      if (attempts >= (iter1 ? POLL_MAX_ATTEMPTS_ITER1 : POLL_MAX_ATTEMPTS)) {
        // PRD-1531: giving up without a word left the question looking ignored.
        // Checked after the thread guard so the banner never lands on another chat.
        setIsAwaitingResponse(false);
        if (iter1 && answered) clearBusyNotice();
        else if (iter1) setTurnOutcome({ code: 'run_timeout' });
        else setError(TIMEOUT_NOTICE);
        return;
      }
      try {
        const state = await apiService.getThreadState(threadId);
        if (activeThreadIdRef.current !== threadId) {
          setIsAwaitingResponse(false);
          return;
        }
        if (isStreamingRef.current) {
          setIsAwaitingResponse(false);
          return;
        }
        const newMsgs = state.values?.messages || [];
        setMessages(newMsgs);
        storeStructuredResponse(extractStructuredResponse(state.values), newMsgs);
        storeOutputViewResults(extractOutputViewResult(state.values), newMsgs);
        const failureNotice = stateFailureNotice(state);
        if (isUnansweredQuery(newMsgs) && failureNotice) {
          // The run failed server-side; more polling cannot change that.
          setIsAwaitingResponse(false);
          if (iter1) setTurnOutcome(failureOutcome(failureNotice));
          else setError(failureNotice);
        } else if (isUnansweredQuery(newMsgs)) {
          if (iter1 && attempts >= 2) {
            // PRD-1617: the asker's run status says whether anything is still answering.
            const fresh = await apiService.getThread(threadId).catch(() => null);
            if (activeThreadIdRef.current !== threadId) return;
            if (fresh?.run && fresh.run.running === false) {
              // The answer can land between the two reads; read once more before calling it failed.
              const again = await apiService.getThreadState(threadId).catch(() => null);
              if (activeThreadIdRef.current !== threadId) return;
              const againMsgs = again?.values?.messages;
              if (againMsgs && !isUnansweredQuery(againMsgs)) {
                pollForPendingResponse(threadId, attempts + 1);
                return;
              }
              setIsAwaitingResponse(false);
              setTurnOutcome({ code: 'error', message: "This question didn't get an answer." });
              return;
            }
            if (fresh?.run) setOwnRun(true);
            if (fresh?.run?.started_at) setRunStartedAt(Date.parse(fresh.run.started_at));
          }
          pollForPendingResponse(threadId, attempts + 1);
        } else {
          if (iter1) {
            // PRD-1617: the answer can be saved before the run's last steps finish, and the
            // thread stays claimed until they do. Keep the progress row until the run has ended,
            // so a follow-up sent the moment the answer shows isn't refused as busy.
            const fresh = await apiService.getThread(threadId).catch(() => null);
            if (activeThreadIdRef.current !== threadId) return;
            if (fresh?.run?.running) {
              pollForPendingResponse(threadId, attempts + 1, true);
              return;
            }
          }
          setIsAwaitingResponse(false);
          clearBusyNotice();
        }
      } catch (e) {
        console.warn('[AskResAI] Pending-response poll failed:', e);
        pollForPendingResponse(threadId, attempts + 1, answered);
      }
    }, _pollDelay(attempts));
  };

  // Under the sharing flag ask for own threads explicitly, so the server's
  // default scope can never mix other people's queries into Saved queries.
  const ownThreadsParams = () => (sharedQueriesEnabled ? { scope: 'mine' } : {});

  const loadSharedThreads = async () => {
    if (!sharedQueriesEnabled) return;
    try {
      const response = await apiService.getThreads({ scope: 'shared' });
      setSharedThreads(response.items || []);
    } catch (err) {
      onShowNotification?.(err.message || 'Failed to load queries shared with you', 'error');
    }
  };

  // Unleash can resolve after mount; pick the shared list up (or drop it) then.
  useEffect(() => {
    if (sharedQueriesEnabled) loadSharedThreads();
    else setSharedThreads([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedQueriesEnabled]);

  const loadThreads = async () => {
    setLoadingThreads(true);
    try {
      const response = await apiService.getThreads(ownThreadsParams());
      setThreads(response.items || []);
    } catch (err) {
      console.error('Failed to load threads:', err);
      setError('Failed to load conversations');
    } finally {
      setLoadingThreads(false);
    }
  };

  // Refresh sidebar + active-thread titles from the server. A conversation can
  // be auto-titled (PRD-1075) after we last saw it — including one abandoned
  // mid-stream, whose handleStreamDone (and its loadThreads) never ran — so
  // re-pull the list and patch changed titles for every thread, not just the
  // one being opened. Best-effort and non-blocking; callers fire-and-forget.
  const refreshThreadTitles = async () => {
    try {
      const items = (await apiService.getThreads(ownThreadsParams())).items || [];
      setThreads((prev) => {
        const changed =
          items.length !== prev.length ||
          items.some((it) => {
            const p = prev.find((t) => t.id === it.id);
            return !p || p.title !== it.title;
          });
        return changed ? items : prev; // avoid a no-op re-render of the whole list
      });
      setActiveThread((prev) => {
        if (!prev) return prev;
        const u = items.find((t) => t.id === prev.id);
        return u && u.title !== prev.title ? { ...prev, title: u.title } : prev;
      });
    } catch (e) {
      console.warn('[AskResAI] thread title refresh failed:', e);
    }
  };

  // `poll: false` syncs the messages without waiting on an unanswered turn, for
  // callers that already know the run is over.
  const loadThreadState = async (threadId, { poll = true } = {}) => {
    // Race-guard: if the user switches threads while this load is in flight,
    // a late response can paint the wrong chat's messages. Bail before every
    // setState if the active thread has changed since this load started.
    const isStale = () => activeThreadIdRef.current !== threadId;
    setThreadLoading(true);
    try {
      const state = await apiService.getThreadState(threadId);
      if (isStale()) return;
      const msgs = state.values?.messages || [];
      setMessages(msgs);
      storeStructuredResponse(extractStructuredResponse(state.values), msgs);
      storeOutputViewResults(extractOutputViewResult(state.values), msgs);
      // PRD-977: hand the thread's prior dataset scope to AvailableDatasets so
      // the chips re-hydrate. Defaults to [] if the server didn't surface the
      // field (older backends), which is a no-op for the chip hydration.
      setThreadDatasetIds(Array.isArray(state.dataset_ids) ? state.dataset_ids : []);
      // PRD-1581: askers are loaded whether or not the query is shared, so other
      // people's questions keep their names after sharing stops or with sharing off.
      if (activeThread?.id === threadId) loadTurns(threadId, isStale);

      // Load checkpoints for edit/regenerate support. A long thread's history is large
      // and slow; the loading state stays up until it arrives. PRD-1571: the server
      // refuses /history (409) on a thread that has ever been shared, and edit and
      // regenerate are hidden there, so don't ask.
      if (sharedQueriesEnabled && activeThread?.id === threadId && activeThread.shared_at) {
        setCheckpoints([]);
      } else {
        try {
          const history = await apiService.getThreadHistory(threadId);
          if (isStale()) return;
          setCheckpoints(history || []);
        } catch (e) {
          if (isStale()) return;
          console.warn('Could not load checkpoints:', e);
          setCheckpoints([]);
        }
      }

      // Check for pending interrupts (skip if tasks have errors — those aren't real HITL interrupts)
      const failureNotice = stateFailureNotice(state);
      if (state.next && state.tasks && !failureNotice) {
        handleInterrupt(state);
      } else if (failureNotice) {
        console.warn('[AskResAI] Agent error in thread state, not treating as interrupt');
      }

      if (isUnansweredQuery(msgs) && failureNotice) {
        // PRD-1531: the last run failed server-side (a reopened or switched-back
        // chat lands here). Say so instead of polling a run that is already over.
        cancelPendingResponsePoll();
        if (iter1) setTurnOutcome(failureOutcome(failureNotice));
        else setError(failureNotice);
      } else if (poll && isUnansweredQuery(msgs) && !isStreamingRef.current) {
        pollForPendingResponse(threadId);
      } else {
        cancelPendingResponsePoll();
        if (!isUnansweredQuery(msgs)) clearBusyNotice();
      }

      // Out of the critical path (fire-and-forget): pick up any server-side
      // auto-title, including for a thread abandoned mid-stream. Deliberately
      // NOT awaited so it never delays the interrupt/poll handling above.
      refreshThreadTitles();
    } catch (err) {
      if (isStale()) return;
      console.error('Failed to load thread state:', err);
    } finally {
      // Only clear if this load is still the active one — a newer load for a
      // different thread owns the indicator otherwise.
      if (!isStale()) setThreadLoading(false);
    }
  };

  const handleInterrupt = (state) => {

    // LangGraph interrupt structure: tasks[].interrupts[].value
    const interruptTask = state.tasks?.find(t => t.interrupts?.length > 0);

    if (interruptTask?.interrupts?.[0]) {
      const interruptData = interruptTask.interrupts[0].value;

      // Check for schema_approval type
      if (interruptData?.type === 'schema_approval') {
        setPendingApproval(interruptData);
        setShowApprovalModal(true);
      }
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Thread Management
  // ─────────────────────────────────────────────────────────────────────────

  const handleNewConversation = async () => {
    cancelPendingResponsePoll();
    // Abort any ongoing streaming
    if (abortStreamRef.current) {
      abortStreamRef.current();
      abortStreamRef.current = null;
    }
    // Reset transient chat view state
    setMessages([]);
    setCheckpoints([]);
    setQuery('');
    setStreamingContent('');
    setStreamingToolCalls([]);
    setIsStreaming(false);
    setLoading(false);
    setError(null);
    setShowApprovalModal(false);
    setPendingApproval(null);
    setStructuredResponses({});
    setOutputViewResults({});
    setShowReasoningForMsgs(new Set());
    setShowEvidenceForMsgs(new Set());
    setThreadDatasetIds([]);

    // PRD-1075: open a real, empty conversation page immediately on the action —
    // don't wait for the first query. If we're already sitting on an untouched
    // blank conversation, reuse it so repeated clicks don't create empty threads.
    if (activeThread && messages.length === 0) {
      return;
    }
    try {
      const newThread = await apiService.createThread();
      skipLoadThreadStateRef.current = true;
      setThreads(prev => [newThread, ...prev]);
      setActiveThread(newThread);
    } catch (err) {
      console.error('Failed to create thread:', err);
      // Fall back to lazy creation on the next send.
      setActiveThread(null);
    }
  };

  const handleSelectThread = async (thread) => {
    cancelPendingResponsePoll();
    seededPinsRef.current = null;
    holdAtEnd();
    myAnswerRef.current = false;
    if (abortStreamRef.current) {
      abortStreamRef.current();
      abortStreamRef.current = null;
    }
    // Clear the previous chat's view synchronously so it doesn't flash on
    // screen while loadThreadState fetches the new one.
    setMessages([]);
    setCheckpoints([]);
    setError(null);
    setActiveThread(thread);
    setShowApprovalModal(false);
    setPendingApproval(null);
    setStreamingContent('');
    setStreamingToolCalls([]);
    setIsStreaming(false);
    setLoading(false);
    setEditingMessageIndex(null);
    setStructuredResponses({});
    setOutputViewResults({});
    // Clear the picker immediately on switch; the fetch effect repopulates it
    // from the new thread's datasets, avoiding a stale-format flash.
    setAvailableFormats([]);
    setSelectedOutputFormat(null);
    setShowReasoningForMsgs(new Set());
    setShowEvidenceForMsgs(new Set());
    setTurnOutcome(null);
    setRunStartedAt(null);
    setOwnRun(false);
    // PRD-1571 R11: a locked query renders from its list row; nothing about the
    // conversation is fetched.
    if (sharedQueriesEnabled && thread.locked) return;
    try {
      const fresh = await apiService.getThread(thread.id);
      setActiveThread(fresh);
      // PRD-1617: back on a thread mid-answer, the progress row counts from the run's start.
      if (fresh?.run?.running && fresh.run.started_at) setRunStartedAt(Date.parse(fresh.run.started_at));
      setOwnRun(!!fresh?.run?.running);
    } catch (err) {
      // A shared query that stopped being shared or was deleted since the list loaded.
      if (iter1 && sharedQueriesEnabled && !isThreadOwner(thread, user) && lostAccessReason(err)) {
        handleLostAccess(thread.id, err);
      }
    }
  };

  const handleDeleteThread = async (threadId, e) => {
    if (e) e.stopPropagation();
    // Warn before deleting a conversation that a report automation delivers into —
    // deleting doesn't stop the automation, but the next run starts a fresh one.
    const thread = threads.find((t) => t.id === threadId);
    if (sharedQueriesEnabled && isThreadShared(thread)) {
      // PRD-1571 R5: deleting a shared query removes it for everyone; say who first.
      const ok = await confirmDelete({
        title: confirmTitle('Delete', thread),
        message: deleteSharedMessage(await countOthersAsked(thread), thread.report_automation_name),
        confirmLabel: 'Delete',
        variant: 'danger',
      });
      if (!ok) return;
    } else if (thread?.report_automation_name) {
      const ok = await confirmDelete({
        title: 'Delete report conversation?',
        message: `This conversation is linked to the scheduled report "${thread.report_automation_name}". `
          + 'Deleting it won’t stop the automation, but its next delivery will start a brand-new '
          + 'conversation. Delete anyway?',
        confirmLabel: 'Delete',
        variant: 'danger',
      });
      if (!ok) return;
    }
    await deleteThreadNow(threadId);
  };

  const deleteThreadNow = async (threadId) => {
    try {
      await apiService.deleteThread(threadId);
      setThreads(prev => prev.filter(t => t.id !== threadId));
      if (activeThread?.id === threadId) {
        setActiveThread(null);
        setMessages([]);
      }
      return true;
    } catch (err) {
      console.error('Failed to delete thread:', err);
      setError('Failed to delete conversation');
      return false;
    }
  };

  // ── Ask ResAI Iteration 1 (PRD-1621..1624) ─────────────────────────────────

  // PRD-1615/1621: a pin is this person's alone; the lists come back re-ordered by the server.
  const handleTogglePin = async (thread) => {
    try {
      if (thread.pinned) await apiService.unpinThread(thread.id);
      else await apiService.pinThread(thread.id);
      announce(thread.pinned ? 'Query unpinned' : 'Query pinned');
      setActiveThread(prev => (prev?.id === thread.id ? { ...prev, pinned: !thread.pinned } : prev));
      await Promise.all([loadThreads(), loadSharedThreads()]);
    } catch (err) {
      setError(thread.pinned ? "Couldn't unpin this query." : "Couldn't pin this query.");
    }
  };

  const openRename = (thread) => {
    setRenameDraft(thread.title || '');
    setRenameTarget(thread);
  };

  const renameError = renameTarget && !renameDraft.trim()
    ? 'Enter a name.'
    : renameDraft.trim().length > 200 ? 'Use 200 characters or fewer.' : null;

  const submitRename = async (e) => {
    e?.preventDefault?.();
    if (!renameTarget || renameError) return;
    const title = renameDraft.trim();
    try {
      await apiService.updateThreadTitle(renameTarget.id, title);
      patchThread(renameTarget.id, { title });
      setSharedThreads(prev => prev.map(t => (t.id === renameTarget.id ? { ...t, title } : t)));
      announce('Query renamed');
      setRenameTarget(null);
    } catch (err) {
      setError("Couldn't rename this query.");
    }
  };

  // PRD-1621: Delete always confirms, in a modal that opens on Cancel.
  const openDelete = async (thread) => {
    const reportNotice = thread.report_automation_name
      ? `This conversation is linked to the scheduled report "${thread.report_automation_name}". `
        + 'Deleting it won\u2019t stop the automation, but its next delivery will start a brand-new conversation.'
      : '';
    let message = reportNotice || 'This deletes the query and its answers. You can\u2019t undo this.';
    if (sharedQueriesEnabled && isThreadShared(thread)) {
      message = [deleteSharedQueryMessage(await countOthersAsked(thread)), reportNotice].filter(Boolean).join(' ');
    }
    setDeleteTarget({ thread, message });
  };

  // Shared Queries handoff: Share and Stop sharing are modal confirmations from the query's menu.
  // The datasets a query uses: the open one's are loaded; any other's come from its state.
  // Read before the modal opens, so it can't be confirmed before it names them. If they
  // can't be read, the modal leaves that line out, as it does for a query with none.
  const sharedDatasetIds = async (thread) => {
    if (thread.id === activeThread?.id && threadDatasetIds.length) return threadDatasetIds;
    if (thread.pinned_dataset_ids?.length) return thread.pinned_dataset_ids;
    try {
      const state = await apiService.getThreadState(thread.id);
      return Array.isArray(state?.dataset_ids) ? state.dataset_ids : [];
    } catch (_) {
      return [];
    }
  };

  const openShare = async (thread) => {
    if (!iter1 || !sharedQueriesEnabled) return;
    const ids = await sharedDatasetIds(thread);
    const datasetNames = ids.map((id) => datasetNameById.get(id)).filter(Boolean);
    setShareTarget({ thread, datasetNames, error: null, busy: false });
  };

  // The row only shows as shared once the server says so. The modal can be closed, or another
  // opened, while the request runs; the result then changes the row, never someone else's modal.
  const forThread = (thread, next) => (prev) => (prev?.thread.id === thread.id ? next(prev) : prev);

  const confirmShare = async () => {
    const { thread } = shareTarget;
    setShareTarget(forThread(thread, (prev) => ({ ...prev, error: null, busy: true })));
    try {
      const updated = await apiService.setThreadVisibility(thread.id, VISIBILITY_ORGANIZATION);
      if (updated?.visibility !== VISIBILITY_ORGANIZATION) throw new Error(SHARE_ERROR);
      patchThread(thread.id, { visibility: updated.visibility, shared_at: updated.shared_at });
      reloadTurnsIfOpen(thread.id);
      refreshShareEvents(thread.id);
      setShareTarget(forThread(thread, () => null));
      announce(`${thread.title || 'Untitled conversation'} is now shared with your organization`);
    } catch (_) {
      setShareTarget(forThread(thread, (prev) => ({ ...prev, error: SHARE_ERROR, busy: false })));
    }
  };

  const openStopSharing = async (thread) => {
    if (!iter1 || !sharedQueriesEnabled) return;
    setStopShareTarget({ thread, others: await countOthersAsked(thread), busy: false });
  };

  const confirmStopSharing = async () => {
    const { thread } = stopShareTarget;
    setStopShareTarget(forThread(thread, (prev) => ({ ...prev, busy: true })));
    try {
      const updated = await apiService.setThreadVisibility(thread.id, VISIBILITY_PRIVATE);
      if (updated?.visibility !== VISIBILITY_PRIVATE) throw new Error('not stopped');
      patchThread(thread.id, { visibility: updated.visibility });
      reloadTurnsIfOpen(thread.id);
      refreshShareEvents(thread.id);
      announce(`${thread.title || 'Untitled conversation'} is no longer shared`);
    } catch (_) {
      setError("Couldn't stop sharing this query.");
    }
    setStopShareTarget(forThread(thread, () => null));
  };

  const confirmDeleteTarget = async () => {
    const { thread } = deleteTarget;
    setDeleteTarget(null);
    if (await deleteThreadNow(thread.id)) announce('Query deleted');
  };

  // PRD-1619: Stop. The question stays and is marked stopped; any partial answer is dropped.
  const handleStop = async () => {
    const threadId = activeThreadIdRef.current;
    if (!threadId) return;
    holdAtEnd();
    myAnswerRef.current = false;
    let result;
    try {
      result = await apiService.stopAnswer(threadId);
    } catch (err) {
      result = null;
    }
    if (!result?.stopped && result?.reason !== 'not_running') {
      // Not stoppable, or the agent couldn't be reached: the answer is still coming, so keep waiting.
      setError("Couldn't stop this answer.");
      return;
    }
    if (result.stopped) announce('Answer stopped');
    cancelPendingResponsePoll();
    // still_stopping: the thread frees shortly, so poll until the stopped mark is written.
    loadThreadState(threadId, { poll: result.reason === 'still_stopping' });
    textareaRef.current?.focus();
  };

  const questionText = (msg) => (typeof msg?.content === 'string'
    ? msg.content.replace(/^\[Datasets?:.*\]\s*/i, '').replace(/^\[Files?: .+\]\s*/i, '')
    : '');

  // A failed turn's error state. The text is one of the fixed notices, never the raw error.
  const failureOutcome = (notice) => (notice === TIMEOUT_NOTICE
    ? { code: 'run_timeout' }
    : { code: 'error', message: notice });

  // Try again after a failure: the last question, sent again over the current selection.
  const retryLastQuestion = () => {
    const last = [...messages].reverse().find((m) => m?.type === 'human');
    const text = questionText(last);
    if (text) handleQuerySubmit(undefined, text);
  };

  // PRD-1623: the datasets each question used, from its "[Datasets: …]" prefix.
  const humanDatasetNames = (index) => {
    const m = messages[index];
    if (m?.type !== 'human') return null;
    const match = (typeof m.content === 'string' ? m.content : '').match(/^\[Datasets?:\s*(.*)\]/i);
    return match ? match[1].split(',').map((n) => n.trim()).filter(Boolean) : null;
  };
  const humanDatasetChange = (index) => {
    const current = humanDatasetNames(index);
    if (!current) return null;
    for (let i = index - 1; i >= 0; i -= 1) {
      if (messages[i]?.type === 'human') return datasetChange(humanDatasetNames(i), current);
    }
    return null;
  };

  // ── Shared queries (PRD-1571) ──────────────────────────────────────────────

  // Author labels for a conversation. A failure leaves them blank rather than
  // guessing; the conversation itself still renders.
  const loadTurns = async (threadId, isStale) => {
    try {
      const list = await apiService.getThreadTurns(threadId);
      if (isStale()) return;
      setTurns(Array.isArray(list) ? list : []);
      lastTurnSeqRef.current = maxTurnSeq(list);
      turnsPrimedRef.current = true;
    } catch (_) {
      if (!isStale()) setTurns([]);
    }
  };

  // PRD-1571 R4: a participant's open shared query answered 403/404. Re-read the
  // Shared with me list to tell why: still listed but locked means a dataset
  // they can't read was attached (Frame 3); gone means the owner stopped
  // sharing it or deleted it. The server answers 404 for both, so they share
  // one statement.
  const handleLostAccess = async (threadId, err) => {
    // Shared Queries handoff: the thread is replaced straight away by why, and announced.
    // Stopped and deleted queries leave Shared by others. Losing access to its data withholds
    // the name from then on; the owner keeps the name and the menu, and both lists re-read so
    // the row shows as no access.
    if (iter1) {
      const reason = lostAccessReason(err) || 'unshared';
      if (activeThreadIdRef.current !== threadId) return;
      cancelPendingResponsePoll();
      setMessages([]);
      setGoneReason(reason);
      setSharingEnded(true);
      if (reason === 'locked') {
        loadThreads();
        loadSharedThreads();
      } else {
        setSharedThreads((prev) => prev.filter((t) => t.id !== threadId));
      }
      announce(reason === 'locked' ? LOST_ACCESS_ANNOUNCEMENT : QUERY_GONE[reason]);
      return;
    }
    let row = null;
    try {
      const items = (await apiService.getThreads({ scope: 'shared' })).items || [];
      setSharedThreads(items);
      row = items.find((t) => t.id === threadId) || null;
    } catch (_) {
      // Can't tell; treat it as no longer shared rather than leave it looking open.
    }
    if (activeThreadIdRef.current !== threadId) return;
    cancelPendingResponsePoll();
    setMessages([]);
    if (row?.locked) setActiveThread(row);
    else setSharingEnded(true);
  };

  // Other people who have asked in a thread, or null if the turns can't be read.
  const countOthersAsked = async (thread) => {
    try {
      const turns = await apiService.getThreadTurns(thread.id);
      return countOtherAskers(turns, thread.user_id || user?.id);
    } catch (_) {
      return null;
    }
  };

  const patchThread = (threadId, patch) => {
    setThreads(prev => prev.map(t => (t.id === threadId ? { ...t, ...patch } : t)));
    if (activeThread?.id === threadId) setActiveThread(prev => (prev ? { ...prev, ...patch } : prev));
  };

  // PRD-1581: after the sharing switch moves, re-read the askers of the open
  // query now rather than on the next poll.
  const reloadTurnsIfOpen = (threadId) => {
    if (activeThreadIdRef.current === threadId) {
      loadTurns(threadId, () => activeThreadIdRef.current !== threadId);
    }
  };

  // PRD-1643: after sharing or stopping, the open query re-reads its share events so the new
  // divider shows straight away. A failure leaves the dividers as they were.
  const refreshShareEvents = (threadId) => {
    if (activeThreadIdRef.current !== threadId) return;
    apiService.getThread(threadId)
      .then((fresh) => {
        if (activeThreadIdRef.current === threadId) patchThread(threadId, { share_events: fresh?.share_events ?? null });
      })
      .catch(() => {});
  };

  const handleOpenShare = (thread) => {
    if (!sharedQueriesEnabled) return;
    setSharePopover({ thread, anchorEl: threadRowRefs.current[thread.id] || null });
  };

  // Closing the popover returns focus to the row's "Query actions" button, as
  // the header does for the notifications bell.
  const closeSharePopover = (thread) => {
    setSharePopover(null);
    threadRowRefs.current[thread.id]?.querySelector('.row-actions-button')?.focus();
  };

  // Throws on failure so the popover can show the error in place (R2). The row
  // only shows as shared once the server says so: a server that accepts the PATCH
  // but ignores `visibility` must not leave the owner believing it is shared.
  const handleShareThread = async (thread) => {
    if (!sharedQueriesEnabled) return;
    const updated = await apiService.setThreadVisibility(thread.id, VISIBILITY_ORGANIZATION);
    if (updated?.visibility !== VISIBILITY_ORGANIZATION) {
      throw new Error('Sharing did not take effect. Try again.');
    }
    patchThread(thread.id, { visibility: updated.visibility, shared_at: updated.shared_at });
    reloadTurnsIfOpen(thread.id);
    closeSharePopover(thread);
  };

  // R4: the owner turns the switch off in Sharing settings (Frame 4).
  const handleStopSharing = async (thread) => {
    if (!sharedQueriesEnabled) return;
    closeSharePopover(thread);
    const ok = await confirmDelete({
      title: confirmTitle('Stop sharing', thread),
      message: stopSharingMessage(await countOthersAsked(thread)),
      confirmLabel: 'Stop sharing',
      cancelLabel: 'Keep sharing',
      variant: 'danger',
    });
    threadRowRefs.current[thread.id]?.querySelector('.row-actions-button')?.focus();
    if (!ok) return;
    try {
      const updated = await apiService.setThreadVisibility(thread.id, VISIBILITY_PRIVATE);
      if (updated?.visibility !== VISIBILITY_PRIVATE) throw new Error('Stopping sharing did not take effect. Try again.');
      patchThread(thread.id, { visibility: updated.visibility });
      reloadTurnsIfOpen(thread.id);
    } catch (err) {
      onShowNotification?.(err.message || 'Failed to stop sharing', 'error');
    }
  };

  // ── Thread rename (PRD-1075) ───────────────────────────────────────────────

  const handleStartRename = (thread, e) => {
    if (e) e.stopPropagation();
    setRenamingThreadId(thread.id);
    setRenameValue(thread.title && thread.title !== 'New conversation' ? thread.title : '');
  };

  const handleCancelRename = () => {
    setRenamingThreadId(null);
    setRenameValue('');
  };

  const handleConfirmRename = async (threadId) => {
    const title = renameValue.trim();
    if (!title) {
      handleCancelRename();
      return;
    }
    // Optimistic update, then persist. A user-set title won't be overwritten by
    // auto-titling (backend keys off the "New conversation" sentinel).
    setThreads(prev => prev.map(t => (t.id === threadId ? { ...t, title } : t)));
    if (activeThread?.id === threadId) setActiveThread(prev => (prev ? { ...prev, title } : prev));
    setRenamingThreadId(null);
    setRenameValue('');
    try {
      await apiService.updateThreadTitle(threadId, title);
    } catch (err) {
      console.error('Failed to rename thread:', err);
      setError('Failed to rename conversation');
      loadThreads();
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Message Handling - Simplified streaming using LangGraph format
  // ─────────────────────────────────────────────────────────────────────────

  const handleQuerySubmit = async (e, resendText) => {
    e?.preventDefault?.();
    // PRD-1624: Try again resends a question without touching what's in the box.
    const askText = resendText ?? query;
    // answerInFlight is render state, so a second Enter during the createThread await
    // below would still pass it; the ref closes that window until loading is set.
    if ((!askText.trim() && selectedFiles.length === 0) || answerInFlight || startingSendRef.current) return;
    // Only a send that really starts makes the answer this viewer's own to follow.
    followMyAnswer();

    cancelPendingResponsePoll();

    let currentThread = activeThread;
    if (!currentThread) {
      startingSendRef.current = true;
      try {
        currentThread = await apiService.createThread();
        skipLoadThreadStateRef.current = true;
        setThreads(prev => [currentThread, ...prev]);
        setActiveThread(currentThread);
      } catch (err) {
        console.error('Failed to create thread:', err);
        setError('Failed to start conversation');
        myAnswerRef.current = false;
        return;
      } finally {
        startingSendRef.current = false;
      }
    }

    // Shared Queries handoff: the owner keeps the normal dataset selector on a shared query. The
    // server answers every turn of it over the pinned datasets, so a changed selection is
    // re-pinned before the question goes, and applies from this turn on.
    // Only a selection the owner changed after opening is re-pinned, and pins they can't see
    // in the list (a failed or hidden dataset) are kept rather than dropped for everyone.
    const seeded = seededPinsRef.current;
    if (iter1 && sharedThreadOpen && !participantView && seeded?.threadId === currentThread.id) {
      const ids = selectedDatasets.map((d) => d.id);
      if (ids.length && (ids.length !== seeded.ids.length || ids.some((id) => !seeded.ids.includes(id)))) {
        const visible = new Set(loadedDatasets.map((d) => d.id));
        const hidden = (currentThread.pinned_dataset_ids || []).filter((id) => !visible.has(id) && !ids.includes(id));
        const next = [...ids, ...hidden];
        try {
          const updated = await apiService.setThreadDatasets(currentThread.id, next);
          patchThread(currentThread.id, { pinned_dataset_ids: updated?.pinned_dataset_ids || next });
          seededPinsRef.current = { threadId: currentThread.id, ids };
        } catch (_) {
          setError("Couldn't change this query's datasets, so your question wasn't sent.");
          myAnswerRef.current = false;
          return;
        }
      }
    }

    // Add user message optimistically (LangGraph format)
    // pending: shown before the server has written it, so it has no time yet.
    const userMessage = { type: 'human', content: askText, pending: true };
    setMessages(prev => [...prev, userMessage]);

    const currentQuery = askText;
    const currentFiles = selectedFiles;
    lastSentRef.current = { text: currentQuery, files: currentFiles, threadId: currentThread.id };
    if (resendText === undefined) setQuery('');
    setSelectedFiles([]);
    setLoading(true);
    setError(null);
    setTurnOutcome(null);
    setRunStartedAt(Date.now());
    setOwnRun(true);
    setStreamingContent('');
    setStreamingToolCalls([]);
    setIsStreaming(true);

    const datasetIds = Array.from(new Set([...selectedDatasets.map(d => d.id), ...moduleSelectedDatasetIds]));

    try {
      if (currentFiles.length > 0) {
        // File upload - non-streaming
        const response = await apiService.sendMessageWithFiles(
          currentThread.id, currentQuery, currentFiles, datasetIds, selectedOutputFormat
        );
        setMessages(response.messages || []);
        storeStructuredResponse(extractStructuredResponse(response), response.messages || []);
        storeOutputViewResults(extractOutputViewResult(response), response.messages || []);
        if (response.interrupt_data?.error) {
          setError(turnFailureNotice(response.interrupt_data.error_message, AGENT_ERROR_NOTICE));
        } else if (response.interrupted && response.interrupt_data) {
          handleInterrupt({ next: response.interrupt_data.next, tasks: response.interrupt_data.tasks });
        }
        setIsStreaming(false);
        setLoading(false);
        await loadThreads();
      } else {
        // Streaming - simplified handler
        abortStreamRef.current = apiService.sendMessageStream(currentThread.id, currentQuery, {
          datasetIds,
          outputFormat: selectedOutputFormat,
          onMessage: handleStreamMessage,
          onDone: handleStreamDone,
          onError: handleStreamError
        });
      }
    } catch (err) {
      setIsStreaming(false);
      setLoading(false);
      if (isThreadBusy(err)) {
        handleThreadBusy();
        return;
      }
      console.error('Failed to send message:', err);
      setError(SEND_FAILED_NOTICE);
    }
  };

  /**
   * Handle streaming messages from LangGraph.
   * LangGraph sends messages in format: [message_chunk, metadata] or {node: {messages: [...]}}
   */
  const handleStreamMessage = (data, eventType) => {
    if (!data) return;

    // Helper: add tool calls with deduplication by ID
    const addToolCalls = (toolCalls) => {
      if (!toolCalls?.length) return;
      setStreamingToolCalls(prev => {
        const existingIds = new Set(prev.map(tc => tc.id));
        const newCalls = toolCalls.filter(tc => tc.id && !existingIds.has(tc.id));
        return newCalls.length > 0 ? [...prev, ...newCalls] : prev;
      });
    };

    // Format 1: Array [message_chunk, metadata] from "messages" stream mode
    if (Array.isArray(data) && data.length > 0) {
      const msg = data[0];
      if (msg?.content) {
        setStreamingContent(prev => prev + msg.content);
      }
      if (msg?.tool_calls?.length > 0) {
        addToolCalls(msg.tool_calls);
      }
      return;
    }

    // Format 2: Object {node_name: {messages: [...]}} from "updates" stream mode
    if (typeof data === 'object' && !Array.isArray(data)) {
      if (data.content) {
        setStreamingContent(prev => prev + data.content);
        return;
      }

      for (const nodeData of Object.values(data)) {
        if (nodeData?.messages) {
          for (const msg of nodeData.messages) {
            if (msg.content && msg.type === 'ai') {
              setStreamingContent(prev => prev + msg.content);
            }
            if (msg.tool_calls?.length > 0) {
              addToolCalls(msg.tool_calls);
            }
          }
        }
      }
    }
  };

  /**
   * Extract structured response from the API response envelope.
   * Supports two shapes:
   *   1. Nested: { structured_response: { response, reasoning, evidence } }
   *   2. Flat:   { response, reasoning, evidence }
   */
  const extractStructuredResponse = (data) => {
    if (!data) return null;
    if (data.structured_response) return data.structured_response;
    if (data.reasoning || data.evidence || data.table_data) {
      return {
        response: data.response || null,
        reasoning: data.reasoning || null,
        evidence: data.evidence || null,
        table_data: data.table_data || null,
        table_decimal_columns: data.table_decimal_columns || null,
      };
    }
    return null;
  };

  /**
   * Rebuild structuredResponses keyed PER AI message from each msg's
   * additional_kwargs.structured_response (written per-message by the
   * orchestrator's attach_structured_response node). Falls back to the
   * conversation-level `fallbackSr` for the latest AI message only when
   * it has no per-message record — covers chats persisted before
   * per-message storage shipped.
   */
  const storeStructuredResponse = (fallbackSr, msgs) => {
    if (!Array.isArray(msgs)) return;
    const map = {};
    let lastAIIdx = -1;
    msgs.forEach((m, i) => {
      if (m?.type !== 'ai' || m.tool_calls?.length) return;
      lastAIIdx = i;
      const sr = m.additional_kwargs?.structured_response;
      if (sr) map[i] = sr;
    });
    if (fallbackSr && lastAIIdx >= 0 && !map[lastAIIdx]) {
      map[lastAIIdx] = fallbackSr;
    }
    setStructuredResponses(map);
  };

  /** Pull the top-level output_view_result off a send/state payload (null-safe). */
  const extractOutputViewResult = (data) => data?.output_view_result ?? null;

  /**
   * Rebuild outputViewResults keyed PER AI message from each msg's
   * additional_kwargs.output_view_result (persisted per-message by the backend,
   * so formatted answers survive a thread reload). Falls back to the top-level
   * `fallbackResult` for the latest AI message only when it has no per-message
   * record — covers the just-completed turn whose result rides the response body.
   */
  const storeOutputViewResults = (fallbackResult, msgs) => {
    if (!Array.isArray(msgs)) return;
    const map = {};
    let lastAIIdx = -1;
    msgs.forEach((m, i) => {
      if (m?.type !== 'ai' || m.tool_calls?.length) return;
      lastAIIdx = i;
      const ovr = m.additional_kwargs?.output_view_result;
      if (ovr) map[i] = ovr;
    });
    if (fallbackResult && lastAIIdx >= 0 && !map[lastAIIdx]) {
      map[lastAIIdx] = fallbackResult;
    }
    setOutputViewResults(map);
  };

  /** Extract the latest export_result from tool messages (use last one so new reports overwrite old). */
  const extractExportFromMessages = (messages) => {
    if (!Array.isArray(messages)) return null;
    let latest = null;
    for (const msg of messages) {
      if (msg?.type !== 'tool' || !msg.content) continue;
      let raw = msg.content;
      if (Array.isArray(raw)) {
        const textPart = raw.find((p) => p?.type === 'text' && typeof p.text === 'string');
        raw = textPart ? textPart.text : null;
      }
      if (typeof raw !== 'string') continue;
      try {
        const data = JSON.parse(raw);
        if (data && typeof data === 'object' && data.blob_path) {
          latest = {
            blob_path: data.blob_path,
            file_name: data.file_name || null,
            format: (data.format || 'pdf').toLowerCase() === 'csv' ? 'csv' : 'pdf',
            export_payload: data.export_payload || null,
          };
        }
      } catch (_) {}
    }
    return latest;
  };

  const handleStreamDone = async (data) => {
    cancelPendingResponsePoll();
    setIsStreaming(false);
    setLoading(false);
    setStreamingContent('');
    setStreamingToolCalls([]);
    const threadId = activeThread?.id;

    const sr = extractStructuredResponse(data);
    const ovr = extractOutputViewResult(data);

    if (data.messages) {
      setMessages(data.messages);
      storeStructuredResponse(sr, data.messages);
      storeOutputViewResults(ovr, data.messages);
      const exportPayload = extractExportFromMessages(data.messages);
      if (exportPayload && threadId) {
        try {
          await apiService.setThreadExport(threadId, exportPayload);
          const listRes = await apiService.getThreads();
          setThreads(listRes.items || []);
          setActiveThread((prev) => {
            const u = (listRes.items || []).find((t) => t.id === prev?.id);
            return u || prev;
          });
        } catch (e) {
          console.warn('[AskResAI] setThreadExport failed:', e);
        }
      }
    }
    if (data.interrupt_data?.error) {
      console.error('[AskResAI] Agent error:', data.interrupt_data.error_message);
      setError(turnFailureNotice(data.interrupt_data.error_message, AGENT_ERROR_NOTICE));
    } else if (data.interrupted && data.interrupt_data) {
      handleInterrupt({ next: data.interrupt_data.next, tasks: data.interrupt_data.tasks });
    }

    loadThreads();
    if (threadId) {
      apiService.getThreadHistory(threadId)
        .then(history => setCheckpoints(history || []))
        .catch(() => {});
    }
  };

  // The agent refused the question because this conversation is still answering an
  // earlier one. Nothing was sent: say so, put the question back in the box,
  // and reload the thread, which drops the optimistic message and waits on the answer.
  const handleThreadBusy = () => {
    const sent = lastSentRef.current;
    if (sent) {
      const { text, files } = sent;
      setError(THREAD_BUSY_NOTICE);
      // Anything typed or attached since stays, after the refused question.
      setQuery((current) => (current && current !== text ? `${text}\n${current}` : text));
      if (files.length) setSelectedFiles((current) => [...files, ...current.filter((f) => !files.includes(f))]);
    } else {
      // An edit or regenerate: nothing to put back, it can simply be retried.
      setError(THREAD_BUSY_RETRY_NOTICE);
    }
    const threadId = activeThreadIdRef.current;
    if (threadId) loadThreadState(threadId);
  };

  const handleStreamError = (err) => {
    setIsStreaming(false);
    setLoading(false);
    setStreamingContent('');
    setStreamingToolCalls([]);
    if (iter1 && err?.code === 'stopped') {
      // PRD-1619: our own Stop. The thread now holds the question, marked stopped.
      const stoppedThread = activeThreadIdRef.current;
      if (stoppedThread) loadThreadState(stoppedThread, { poll: false });
      return;
    }
    if (isThreadBusy(err)) {
      handleThreadBusy();
      return;
    }
    console.error('Stream error:', err);
    const threadId = activeThreadIdRef.current;
    // Iteration 1: only a refusal that means the query went (lostAccessReason); an owner loses
    // a thread only by losing access to the data of one that was ever shared.
    const lost = iter1 ? lostAccessReason(err) : null;
    if (threadId && (iter1
      ? lost && (participantView || (everShared && lost === 'locked'))
      : participantView && (err?.status === 403 || err?.status === 404))) {
      handleLostAccess(threadId, err);
      return;
    }
    if (err?.connectionLost) {
      // The connection dropped, but the backend may still finish the turn.
      // loadThreadState polls for it and shows the banner if the run failed or
      // never lands, so no banner yet: it would sit beside "Thinking".
      if (threadId) loadThreadState(threadId);
      return;
    }
    // The server or the request reported the failure, so the run is over:
    // sync the messages without starting a poll.
    if (iter1) {
      // PRD-1620/1624: the coded failure shows as an error state with Try again.
      setTurnOutcome(err?.code && err.code !== 'error'
        ? { code: err.code, message: err.message, ...(err.elapsedS ? { elapsedMs: err.elapsedS * 1000 } : {}) }
        : failureOutcome(turnFailureNotice(err?.message, SEND_FAILED_NOTICE)));
      if (threadId) loadThreadState(threadId, { poll: false });
      return;
    }
    setError(turnFailureNotice(err?.message, SEND_FAILED_NOTICE));
    if (threadId) loadThreadState(threadId, { poll: false });
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Edit & Regenerate using LangGraph Checkpoints
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Find checkpoint to use when regenerating the AI response for the human at humanMessageIndex.
   * We need the state *before* that human message was added, so that when we re-send the message
   * the graph runs from a clean state and produces a fresh AI response (instead of continuing from
   * a later checkpoint that may already have tool results like "no files").
   * History is typically newest-first: checkpoints[0] = latest, so "before human at index N"
   * = checkpoint at chronological index N-1 = checkpoints[length - (N-1) - 1] = checkpoints[length - N].
   */
  const findCheckpointBeforeMessage = (messageIndex) => {
    if (checkpoints.length === 0) return null;
    // Chronological index of the checkpoint we want: state before this human message = after (humanMessageIndex - 1) messages
    const chronologicalIndex = messageIndex - 1;
    if (chronologicalIndex < 0) return null;
    // Newest-first: index in array = length - 1 - chronologicalIndex
    const targetIndex = checkpoints.length - 1 - chronologicalIndex;
    const idx = Math.max(0, Math.min(targetIndex, checkpoints.length - 1));
    return checkpoints[idx]?.checkpoint_id ?? null;
  };

  const handleStartEdit = (messageIndex, content) => {
    setEditingMessageIndex(messageIndex);
    setEditContent(content);
  };

  const handleCancelEdit = () => {
    setEditingMessageIndex(null);
    setEditContent('');
  };

  const handleSaveEdit = async (messageIndex) => {
    if (!activeThread || !editContent.trim() || answerInFlight) return;
    followMyAnswer();

    const checkpointId = findCheckpointBeforeMessage(messageIndex);
    // An edit forks from a checkpoint; the composer can't carry that, so a refusal
    // does not put it there.
    lastSentRef.current = null;

    setEditingMessageIndex(null);
    setLoading(true);
    setIsStreaming(true);
    setTurnOutcome(null);
    setRunStartedAt(Date.now());
    setOwnRun(true);
    setStreamingContent('');
    setStreamingToolCalls([]);
    setStructuredResponses({});
    setOutputViewResults({});

    // Optimistically show the edited message
    setMessages(prev => [
      ...prev.slice(0, messageIndex),
      { type: 'human', content: editContent, pending: true }
    ]);

    const editedContent = editContent;
    setEditContent('');

    abortStreamRef.current = apiService.sendMessageStream(activeThread.id, editedContent, {
      datasetIds: Array.from(new Set([...selectedDatasets.map(d => d.id), ...moduleSelectedDatasetIds])),
      checkpointId,  // Fork from checkpoint
      outputFormat: selectedOutputFormat,
      onMessage: handleStreamMessage,
      onDone: handleStreamDone,
      onError: handleStreamError
    });
  };

  const handleRegenerateResponse = async (messageIndex) => {
    if (!activeThread || answerInFlight) return;

    // Find the human message before this AI message
    let humanMessageIndex = messageIndex - 1;
    while (humanMessageIndex >= 0 && messages[humanMessageIndex]?.type !== 'human') {
      humanMessageIndex--;
    }
    if (humanMessageIndex < 0) return;
    followMyAnswer();

    const humanMessage = messages[humanMessageIndex];
    const checkpointId = findCheckpointBeforeMessage(humanMessageIndex);

    // Normalize content to string (API expects string; LangGraph may give array of parts)
    const contentToSend = typeof humanMessage.content === 'string'
      ? humanMessage.content
      : Array.isArray(humanMessage.content)
        ? humanMessage.content.map((p) => (p?.text ?? p?.content ?? '')).filter(Boolean).join(' ').trim()
        : String(humanMessage.content ?? '');

    lastSentRef.current = null;
    // Truncate to before the AI response
    setMessages(prev => prev.slice(0, humanMessageIndex + 1));

    setLoading(true);
    setIsStreaming(true);
    setTurnOutcome(null);
    setRunStartedAt(Date.now());
    setOwnRun(true);
    setStreamingContent('');
    setStreamingToolCalls([]);
    setStructuredResponses({});
    setOutputViewResults({});

    abortStreamRef.current = apiService.sendMessageStream(activeThread.id, contentToSend, {
      datasetIds: Array.from(new Set([...selectedDatasets.map(d => d.id), ...moduleSelectedDatasetIds])),
      checkpointId,  // Fork from checkpoint (state before this human message)
      outputFormat: selectedOutputFormat,
      onMessage: handleStreamMessage,
      onDone: handleStreamDone,
      onError: handleStreamError
    });
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Other Handlers
  // ─────────────────────────────────────────────────────────────────────────

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(prev => [...prev, ...files]);
    e.target.value = '';
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleCopyContent = async (content) => {
    try {
      await navigator.clipboard.writeText(content);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleApproval = async (action, feedback = '') => {
    if (!activeThread) return;

    setShowApprovalModal(false);
    setLoading(true);
    // Clear streaming state to prevent multiple "Thinking..." messages
    setStreamingContent('');
    setStreamingToolCalls([]);
    setIsStreaming(false);

    try {
      const response = await apiService.resumeThread(activeThread.id, action, feedback);

      if (response.messages) {
        setMessages(response.messages);
        storeStructuredResponse(extractStructuredResponse(response), response.messages);
        storeOutputViewResults(extractOutputViewResult(response), response.messages);
      }

      if (response.interrupted && response.interrupt_data) {
        handleInterrupt({
          next: response.interrupt_data.next,
          tasks: response.interrupt_data.tasks
        });
      }
    } catch (err) {
      console.error('Failed to resume thread:', err);
      setError('Failed to submit approval. Please try again.');
    } finally {
      setLoading(false);
      setPendingApproval(null);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleQuerySubmit(e);
    }
  };

  /** Download always uses the latest report: refetch thread from backend then download. */
  const handleExportDownload = async () => {
    if (!activeThread?.id) return;
    setError(null);
    try {
      const fresh = await apiService.getThread(activeThread.id);
      if (!fresh?.export_blob_path) {
        setError('No report available. Generate a new report or ask the assistant to regenerate.');
        return;
      }
      setActiveThread((prev) => (prev?.id === fresh.id ? { ...prev, ...fresh } : prev));
      setThreads((prev) => prev.map((t) => (t.id === fresh.id ? { ...t, ...fresh } : t)));
      const { blob } = await apiService.downloadThreadExport(fresh.id);
      const ext = (fresh.export_format || 'pdf').toLowerCase();
      const rawName = (fresh.export_file_name || '').replace(/\.[^.]+$/, '').replace(/[-_]\d{8}_\d{6}$/, '').replace(/[-_]/g, ' ').trim();
      const baseName = rawName || 'ResAI Report';
      const exportDate = fresh.export_created_at ? new Date(fresh.export_created_at) : new Date();
      const localDate = exportDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
      const localTime = exportDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).replace(' ', '');
      const downloadName = `${baseName} ${localDate} ${localTime}.${ext}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = downloadName;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'This export has expired. Ask the assistant to regenerate the report.');
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Message Rendering - LangGraph Native Types
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Get tool results that match the tool calls from an AI message.
   * In LangGraph, tool messages follow the AI message that called them.
   */
  const getToolResultsForMessage = (messageIndex) => {
    // Walk backwards collecting tool messages. No hard human boundary so that
    // follow-up requests ("print as a table") can still find tool results from
    // the preceding extraction turn for evidence merging.
    const results = [];
    for (let i = messageIndex - 1; i >= 0; i--) {
      if (messages[i]?.type === 'tool') {
        results.push(messages[i]);
      }
    }
    return results;
  };

  /**
   * Parse tool messages and return list of { extracted_data } from extract_data tool results.
   * Used to merge _evidence into extraction table when the LLM omits it from the response.
   */
  const getExtractionPayloadsFromToolResults = (messageIndex) => {
    const payloads = [];
    const toolMessages = getToolResultsForMessage(messageIndex);
    for (const tm of toolMessages) {
      if (!tm.content) continue;
      try {
        const parsed = JSON.parse(tm.content);
        if (!parsed) continue;
        // Single-file extract_data result
        if (parsed.extracted_data) {
          payloads.push(parsed.extracted_data);
        }
        // Batch extract_dataset_batch result: { result: { results: [{ data: { extracted_data } }] } }
        if (parsed.result?.results && Array.isArray(parsed.result.results)) {
          for (const item of parsed.result.results) {
            if (item?.data?.extracted_data) payloads.push(item.data.extracted_data);
          }
        }
      } catch (_) {}
    }
    return payloads;
  };

  /** Check if extraction payloads for a message have evidence data. */
  const hasExtractionEvidence = (messageIndex) => {
    const payloads = getExtractionPayloadsFromToolResults(messageIndex);
    return payloads.some(p => p && p.evidence);
  };

  /**
   * Pull table extractions out of an AI message's `msg.content` and merge any
   * matching tool-result evidence into the rows. Used by BOTH the
   * structured-response branch and the fallback branch so a table emitted by
   * the ReAct-loop LLM in `msg.content` renders regardless of whether the
   * structured-output call also surfaced it via `sr.table_data`.
   */
  const getMergedExtractionsForMessage = (content, messageIndex) => {
    const { extractions } = parseExtractionResults(content);
    if (extractions.length === 0) return [];
    const toolPayloads = getExtractionPayloadsFromToolResults(messageIndex);
    let payloadIndex = 0;
    return extractions.map((ext) => {
      const data = ext.data;
      const rows = Array.isArray(data) ? data : [data];
      if (toolPayloads.length === 0) return { data: ext.data, title: ext.title };
      const mergedRows = rows.map((row) => {
        const fromTool = toolPayloads[payloadIndex];
        if (fromTool && fromTool.evidence) {
          payloadIndex += 1;
          return { ...row, evidence: fromTool.evidence };
        }
        if (fromTool) payloadIndex += 1;
        return row;
      });
      const mergedData = Array.isArray(data) ? mergedRows : mergedRows[0];
      return { data: mergedData, title: ext.title };
    });
  };

  /**
   * Walk backward from an AI message to find the nearest preceding human
   * message and check whether the user explicitly asked for a table
   * ("as a table", "in tabular form", "in rows", etc.). Used to gate the
   * `msg.content` markdown-table extraction in the sr branch — without this
   * check the LLM's opportunistic tables get rendered for prompts that
   * didn't ask for one.
   */
  const TABLE_REQUEST_RE = /\btables?\b|\btabular\b|\bin\s+(?:a\s+)?rows?\b/i;
  const userAskedForTable = (aiIndex) => {
    for (let i = aiIndex - 1; i >= 0; i--) {
      const m = messages[i];
      if (m?.type !== 'human') continue;
      const raw = typeof m.content === 'string' ? m.content : '';
      // Strip the [Datasets: <uuids>] prefix that the UI prepends to human
      // messages — it can contain words that match the regex by coincidence.
      const cleaned = raw.replace(/^\[Datasets?:.*\]\s*/i, '').replace(/^\[Files?: .+\]\s*/i, '');
      return TABLE_REQUEST_RE.test(cleaned);
    }
    return false;
  };

  /**
   * Extract contiguous GFM-table blocks from a text. Only returns blocks
   * that contain a separator row (`|---|---|`) so we don't false-match prose
   * that happens to contain pipes. Returned strings are passed straight to
   * ReactMarkdown which renders them natively via remark-gfm.
   */
  const extractMarkdownTables = (text) => {
    if (!text || typeof text !== 'string') return [];
    const blocks = [];
    let current = [];
    for (const line of text.split('\n')) {
      const trimmed = line.trim();
      if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
        current.push(line);
      } else {
        if (current.length > 0) blocks.push(current);
        current = [];
      }
    }
    if (current.length > 0) blocks.push(current);
    return blocks
      .filter(b => b.some(l => /^\s*\|[-:\s|]+\|\s*$/.test(l)))
      .map(b => b.join('\n'));
  };

  /**
   * Get visible messages with original indices.
   * Tool messages are always filtered out - they're not displayed to users.
   * Also filter out AI messages that only have tool_calls but no content (these are shown as "Thinking..." during processing).
   */
  const getVisibleMessages = () => {
    const filtered = messages
      .map((msg, originalIndex) => ({ msg, originalIndex }))
      .filter(({ msg }) => {
        // Filter out tool messages
        if (msg.type === 'tool') return false;
        // Filter out AI messages that have tool_calls — these are intermediate
        // "planning" messages (e.g. "I will now extract...") not final responses
        if (msg.type === 'ai' && msg.tool_calls?.length > 0) {
          return false;
        }
        return true;
      });

    return filtered;
  };

  /**
   * Normalize message content to a string (API may return string or array of content blocks).
   */
  const normalizeContentToString = (content) => {
    if (content == null) return '';
    if (typeof content === 'string') return content.trim();
    if (Array.isArray(content)) {
      return content
        .map((part) => (part?.text != null ? part.text : part?.content != null ? part.content : ''))
        .filter(Boolean)
        .join(' ')
        .trim();
    }
    return String(content).trim();
  };

  const handleScheduleReportClick = (originalIndex, msgContent) => {
    setOpenActionsMenuIndex(null);
    // Find the last human message before this AI message (may be separated by tool messages)
    let prevHuman = null;
    for (let i = originalIndex - 1; i >= 0; i--) {
      if (messages[i]?.type === 'human') {
        prevHuman = messages[i];
        break;
      }
    }
    const queryText = prevHuman ? normalizeContentToString(prevHuman.content) : '';
    // When a formatted view is shown the prose is suppressed, so preview the
    // formatted answer the user actually sees, not the hidden content.
    const ovr = outputViewResults[originalIndex];
    const responsePreview = ovr
      ? outputViewResultToText(ovr)
      : normalizeContentToString(msgContent);
    const payload = {
      queryText,
      responsePreview: responsePreview || undefined,
      threadId: activeThread?.id || undefined,
      datasetIds: Array.from(new Set([...selectedDatasets.map(d => d.id), ...moduleSelectedDatasetIds])),
    };
    if (onScheduleReport) onScheduleReport(payload);
  };

  /**
   * Get dataset names for a specific AI message by looking at the
   * preceding human message's [Datasets: name] prefix.
   * Each turn keeps its own sources — swapping datasets mid-conversation
   * won't change earlier messages' sources.
   */
  const getSourceNamesForMessage = (aiOriginalIndex) => {
    // Walk backwards to find the human message for this turn
    for (let i = aiOriginalIndex - 1; i >= 0; i--) {
      const m = messages[i];
      if (m?.type !== 'human') continue;
      const text = typeof m.content === 'string' ? m.content : '';
      const match = text.match(/^\[Datasets?:\s*(.*)\]/i);
      if (match) {
        return match[1].split(',').map(n => n.trim()).filter(Boolean);
      }
      break; // found human message but no prefix
    }
    // Fallback for the latest turn: use currently selected datasets
    if (selectedDatasets.length > 0) {
      return selectedDatasets.map(ds => ds.name);
    }
    return [];
  };

  // PRD-1359: the datasets this thread was GROUNDED on, for the review banner.
  //
  // The feedback doc's trigger is "if a dataset has been queried which requires
  // review" — the dataset the query ran against, which is `dataset_ids` on the
  // thread. An earlier cut of this resolved the "Sources used" labels by name
  // instead, which looked equivalent and is not: that line often carries a FILE
  // ("782 Bridgewwater M188.pdf") when a turn is scoped to one, so the name
  // matched no dataset and the banner silently never rendered. Ids, not labels.
  //
  // Thread-level rather than per-message on purpose: every answer in a thread
  // grounded on a parked dataset was computed without those documents, so every
  // one of them carries the caveat.
  const reviewBannerDatasets = useMemo(() => {
    const ids = threadDatasetIds.length > 0
      ? threadDatasetIds
      // Live turn, before the thread state round-trips.
      : selectedDatasets.map((d) => d.id);
    if (!ids.length || !loadedDatasets.length) return [];
    return ids
      .map((id) => loadedDatasets.find((d) => String(d.id) === String(id)))
      .filter(Boolean);
  }, [threadDatasetIds, selectedDatasets, loadedDatasets]);

  // Filename base for any CSV downloaded from a table in this thread.
  // Format produced by buildCsvFilename: "{title}_YYYY-MM-DD.csv".
  const csvFilenameBase = activeThread?.title?.trim() || 'conversation';
  const markdownTableComponents = useMemo(
    () => ({
      ...makeMarkdownTableComponents(csvFilenameBase),
      // PRD-1337: a drill-down turn can carry its chart as a ```resai-chart fenced
      // block (the widget's frozen result at that turn). Render it inline as the chart
      // instead of showing the JSON, so every response shows its chart and a refresh
      // turn's chart can be compared against the earlier ones. Non-chart code blocks
      // fall through to the default <pre>.
      pre: (props) => {
        const codeEl = React.Children.toArray(props.children).find(
          (c) => c?.props?.className?.includes?.('language-resai-chart'),
        );
        if (codeEl) {
          const raw = React.Children.toArray(codeEl.props.children).join('');
          try {
            return <DrilldownChartPanel chart={JSON.parse(raw)} />;
          } catch {
            return <pre {...props} />;
          }
        }
        return <pre {...props} />;
      },
    }),
    [csvFilenameBase],
  );

  // PRD-1571 R8: the turn (asker) behind a message — a question's own turn, or
  // for an answer the question just before it. Human message ids are turn ids.
  const turnsById = useMemo(() => new Map(turns.map((t) => [t.id, t])), [turns]);
  // PRD-1581: someone other than this viewer asked here (a deleted user's turn,
  // with no author, counts), so questions are drawn per asker even when private.
  const othersAsked = useMemo(() => turns.some((t) => t.author_id !== user?.id), [turns, user?.id]);
  // PRD-1571 R10: names for the "datasets changed" line, from the panel's list.
  const datasetNameById = useMemo(
    () => new Map(loadedDatasets.map((d) => [d.id, d.name])),
    [loadedDatasets],
  );
  const askerTurnFor = (index) => {
    for (let i = index; i >= 0; i -= 1) {
      if (messages[i]?.type === 'human') return turnsById.get(messages[i].id) || null;
    }
    return null;
  };

  const renderMessage = (msg, originalIndex) => {
    const isHuman = msg.type === 'human';
    const isAI = msg.type === 'ai';

    const isEditing = editingMessageIndex === originalIndex;
    const authorName = user?.profile?.display_name || user?.name || 'You';
    // Long form: chat is read, not scanned. A message still on its way has no time yet.
    const msgTime = msg.pending ? null : <Timestamp value={sentAt(msg)} form="long" />;
    // A drill-down turn also says when the chart it describes was computed.
    const computedAt = isAI ? msg.additional_kwargs?.computed_at : null;
    // PRD-1571 R8: in a shared conversation the asker comes from /turns. Until
    // those load nobody is named, rather than every question showing as yours.
    // PRD-1581: the same holds once sharing stops, except that a question with
    // no turn yet is the owner's, since nobody else can ask in a private query.
    const askerTurn = askersShown ? askerTurnFor(originalIndex) : null;
    const askedByOther = askersShown && (askerTurn ? askerTurn.author_id !== user?.id : sharedThreadOpen);
    const askerKnown = askersShown && (askerTurn || !sharedThreadOpen);
    const askerName = askerKnown ? (askedByOther ? askerTurn.author_name : authorName) : '';

    // Shared Queries handoff: someone else's question sits on the left, like ResAI's answers.
    const otherOnLeft = iter1 && isHuman && askedByOther;

    const message = (
      <div key={`msg-${originalIndex}`} className={`message ${isHuman ? 'user' : 'assistant'}${otherOnLeft ? ' message--other' : ''}`}>
        {/* PRD-09: author identity — user (time · name · avatar) / ResAI (logo · name · time). */}
        <div className="message-author">
          {otherOnLeft ? (
            <>
              {askerTurn && (
                <>
                  <PersonAvatar
                    name={askerTurn.author_name || ''}
                    photoUrl={askerPhotos[askerTurn.author_id] || null}
                    color={askerTurn.author_avatar_color || ''}
                  />
                  <span className="message-author-name">{askerTurn.author_name}</span>
                </>
              )}
              {msgTime && <span className="message-time">{msgTime}</span>}
            </>
          ) : isHuman && askedByOther ? (
            <>
              {msgTime && <span className="message-time">{msgTime}</span>}
              {askerTurn && (
                <>
                  <span className="message-author-name">{askerTurn.author_name}</span>
                  <span
                    className="message-avatar message-avatar--initials"
                    aria-hidden="true"
                    style={initialsStyle(askerTurn.author_name, '')}
                  >
                    {initialsOf(askerTurn.author_name)}
                  </span>
                </>
              )}
            </>
          ) : isHuman ? (
            <>
              {msgTime && <span className="message-time">{msgTime}</span>}
              <span className="message-author-name">{askersShown && !iter1 ? `${authorName} (you)` : authorName}</span>
              {userAvatarUrl ? (
                <img
                  src={userAvatarUrl}
                  alt=""
                  aria-hidden="true"
                  className={`message-avatar ${userAvatarKind === 'logo' ? 'message-avatar--logo' : 'message-avatar--photo'}`}
                />
              ) : (
                <span
                  className="message-avatar message-avatar--initials"
                  aria-hidden="true"
                  style={initialsStyle(authorName, userAvatarColor)}
                >
                  {initialsOf(authorName)}
                </span>
              )}
            </>
          ) : (
            <>
              <img src="/resai-logo.svg" alt="" className="message-avatar message-avatar--logo" aria-hidden="true" />
              <span className="message-author-name">ResAI</span>
              {msgTime && <span className="message-time">{msgTime}</span>}
              {computedAt && (
                <span className="message-time">
                  · Chart computed <Timestamp value={computedAt} form="long" />
                </span>
              )}
              {askerName && (iter1
                ? <ForChip name={askerName} you={!askedByOther} />
                : <Chip className="shared-scope-label">{answeredForLabel(askerName)}</Chip>)}
            </>
          )}
        </div>
        <div className="message-content">
          {isEditing ? (
            <div className="message-edit">
              <textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                autoFocus
              />
              <div className="edit-actions">
                <button
                  onClick={() => handleSaveEdit(originalIndex)}
                  disabled={answerInFlight}
                  title={answerInFlight ? STILL_ANSWERING_HINT : undefined}
                >
                  Save and Submit
                </button>
                <button onClick={handleCancelEdit}>Cancel</button>
              </div>
            </div>
          ) : (
            <div className="message-text">
              {isHuman && <p>{typeof msg.content === 'string' ? msg.content.replace(/^\[Datasets?:.*\]\s*/i, '').replace(/^\[Files?: .+\]\s*/i, '') : msg.content}</p>}

              {isAI && (() => {
                // A chosen output format renders as the formatted view only —
                // the prose answer is suppressed for this turn by design.
                const ovr = outputViewResults[originalIndex];
                if (ovr) {
                  return <OutputViewResult result={ovr} />;
                }

                const sr = structuredResponseEnabled && structuredResponses[originalIndex];

                if (sr) {
                  const mainText = hideDownloadLinkInText(sr.response || '');
                  const tableData = sr.table_data;
                  // Intent-gated extraction: only pull markdown tables out of
                  // msg.content when the user explicitly asked for a table in
                  // the preceding human message AND no other table source will
                  // render. Priority for PRD-898 dedupe: sr.response markdown
                  // table > sr.table_data > extraTables. At most one renders.
                  const mainTextHasTable = /\n\|[-:\s|]+\|\n/.test(mainText);
                  const hasStructuredTable = Array.isArray(tableData) && tableData.length > 0;
                  const extraTables = (
                    userAskedForTable(originalIndex)
                    && !mainTextHasTable
                    && !hasStructuredTable
                  )
                    ? extractMarkdownTables(typeof msg.content === 'string' ? msg.content : '')
                    : [];
                  const checks = Array.isArray(sr.checks) ? sr.checks : [];
                  return (
                    <>
                      {mainText && (
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownTableComponents}>
                          {mainText}
                        </ReactMarkdown>
                      )}
                      {extraTables.map((t, i) => (
                        <ReactMarkdown key={`md-tbl-${i}`} remarkPlugins={[remarkGfm]} components={markdownTableComponents}>
                          {t}
                        </ReactMarkdown>
                      ))}
                      {/* PRD-898: dedupe sr.table_data against a Markdown table already
                          rendered from sr.response. Same rule used a few lines above
                          for extraTables. */}
                      {Array.isArray(tableData) && tableData.length > 0 && !mainTextHasTable && (
                        <ExtractionTable data={tableData} decimalColumns={sr.table_decimal_columns} title="Results" variant="data" filenameBase={csvFilenameBase} checks={checks} evidenceOpen={showExtractionEvidenceForMsgs.has(originalIndex)} />
                      )}
                      {/* Reasoning & Evidence shown inline when toggled from 3-dot menu */}
                      {(showReasoningForMsgs.has(originalIndex) || showEvidenceForMsgs.has(originalIndex)) && (
                        <StructuredResponsePanel
                          reasoning={showReasoningForMsgs.has(originalIndex) ? sr.reasoning : null}
                          evidence={showEvidenceForMsgs.has(originalIndex) ? sr.evidence : null}
                        />
                      )}
                    </>
                  );
                }

                // Fallback: existing extraction-based rendering
                if (!msg.content) return null;
                const { text } = parseExtractionResults(msg.content);
                const textToShow = text ? hideDownloadLinkInText(text) : '';
                const mergedExtractions = getMergedExtractionsForMessage(msg.content, originalIndex);
                return (
                  <>
                    {textToShow && (
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownTableComponents}>
                        {textToShow}
                      </ReactMarkdown>
                    )}
                    {mergedExtractions.map((ext, i) => (
                      <ExtractionTable key={i} data={ext.data} title={ext.title} variant="data" filenameBase={csvFilenameBase} evidenceOpen={showExtractionEvidenceForMsgs.has(originalIndex)} />
                    ))}
                  </>
                );
              })()}
            </div>
          )}

          {/* PRD-1359: above Sources used, not beside it — this answer was
              computed without the parked documents, so the caveat has to be read
              before the source list it qualifies. Deliberately OUTSIDE
              `.message-actions-wrap`: that is a nowrap flex ROW (sources on the
              left, the copy/retry actions on the right), so a banner placed
              inside it lands as a third column rather than above. */}
          {!isEditing && isAI && (
            <ReviewRequiredBanner
              datasets={reviewBannerDatasets}
              variant="resai"
              className="sources-used-review-banner"
            />
          )}

          {/* Message Actions - bottom right, always visible */}
          {!isEditing && (
            <div className="message-actions-wrap">
              {/* Sources used — per-message, from preceding human message */}
              {isAI && !iter1 && (() => {
                const names = getSourceNamesForMessage(originalIndex);
                if (!names.length) return null;
                return (
                  <div className="sources-used">
                    <div className="sources-used-label">Sources used:</div>
                    <div className="sources-used-list">
                      {names.map((name, si) => (
                        <span key={si} className="sources-used-tag">{name}</span>
                      ))}
                    </div>
                  </div>
                );
              })()}
              <div className="message-actions">
              {iter1 ? (
                <>
                  <button
                    type="button"
                    className="iter1-icon-btn"
                    aria-label="Copy"
                    data-tip="Copy"
                    onClick={() => handleCopyContent(
                      isAI && outputViewResults[originalIndex]
                        ? outputViewResultToText(outputViewResults[originalIndex])
                        : (isHuman ? questionText(msg) : (msg.content || ''))
                    )}
                  >
                    <Icon name="copy" size={16} />
                  </button>
                  {isHuman && chatMessageEditEnabled && !everShared && (
                    <button type="button" className="iter1-icon-btn" aria-label="Edit" data-tip="Edit" onClick={() => handleStartEdit(originalIndex, msg.content)}>
                      <Icon name="edit" size={16} />
                    </button>
                  )}
                  {/* Shared Queries handoff: answers in a query that was ever shared are stored,
                      never regenerated, so Retry stays, disabled, and its tooltip says why. */}
                  {isAI && msg.content && everShared && (
                    <DisabledAction icon="arrow-clockwise" label="Retry" note="Answers in shared queries stay as first given" />
                  )}
                  {isAI && msg.content && !everShared && (
                    <button
                      type="button"
                      className="iter1-icon-btn"
                      aria-label="Retry"
                      data-tip="Retry"
                      disabled={answerInFlight}
                      onClick={() => handleRegenerateResponse(originalIndex)}
                    >
                      <Icon name="arrow-clockwise" size={16} />
                    </button>
                  )}
                  {isAI && structuredResponseEnabled && structuredResponses[originalIndex]?.reasoning && (
                    <button
                      type="button"
                      className="iter1-icon-btn"
                      aria-label="Show reasoning"
                      data-tip="Show reasoning"
                      aria-pressed={showReasoningForMsgs.has(originalIndex)}
                      onClick={() => setShowReasoningForMsgs(prev => {
                        const next = new Set(prev);
                        next.has(originalIndex) ? next.delete(originalIndex) : next.add(originalIndex);
                        return next;
                      })}
                    >
                      <Icon name="lightbulb-filament" size={16} />
                    </button>
                  )}
                  {/* R21: only the owner can automate a shared query. */}
                  {isAI && participantView && (
                    <DisabledAction icon="lightning" label="Automate" note={`Only ${sharedOwnerName} can automate this query`} />
                  )}
                  {isAI && !participantView && (
                    <button
                      type="button"
                      className="iter1-icon-btn"
                      aria-label="Automate"
                      data-tip="Automate"
                      onClick={() => handleScheduleReportClick(originalIndex, msg.content)}
                    >
                      <Icon name="lightning" size={16} />
                    </button>
                  )}
                </>
              ) : (<>
              <button
                onClick={() => handleCopyContent(
                  isAI && outputViewResults[originalIndex]
                    ? outputViewResultToText(outputViewResults[originalIndex])
                    : (msg.content || '')
                )}
                title="Copy"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                </svg>
              </button>
              {isHuman && chatMessageEditEnabled && !everShared && (
                <button onClick={() => handleStartEdit(originalIndex, msg.content)} title="Edit">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </button>
              )}
              {isAI && msg.content && !everShared && (
                <button
                  onClick={() => handleRegenerateResponse(originalIndex)}
                  title="Refresh"
                  disabled={answerInFlight}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="1 4 1 10 7 10" />
                    <polyline points="23 20 23 14 17 14" />
                    <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" />
                  </svg>
                </button>
              )}
              {isAI && (
                <div className="message-actions-dropdown">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenActionsMenuIndex(openActionsMenuIndex === originalIndex ? null : originalIndex);
                    }}
                    title="More actions"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <circle cx="12" cy="5" r="1.5" />
                      <circle cx="12" cy="12" r="1.5" />
                      <circle cx="12" cy="19" r="1.5" />
                    </svg>
                  </button>
                  {openActionsMenuIndex === originalIndex ? (
                    <div className="message-actions-dropdown-menu">
                      {structuredResponseEnabled && structuredResponses[originalIndex]?.reasoning && (
                        <button
                          type="button"
                          onClick={() => {
                            setShowReasoningForMsgs(prev => {
                              const next = new Set(prev);
                              next.has(originalIndex) ? next.delete(originalIndex) : next.add(originalIndex);
                              return next;
                            });
                            setOpenActionsMenuIndex(null);
                          }}
                        >
                          {showReasoningForMsgs.has(originalIndex) ? 'Hide reasoning' : 'Show reasoning'}
                        </button>
                      )}
                      {structuredResponseEnabled && structuredResponses[originalIndex]?.evidence?.length > 0 && (
                        <button
                          type="button"
                          onClick={() => {
                            setShowEvidenceForMsgs(prev => {
                              const next = new Set(prev);
                              next.has(originalIndex) ? next.delete(originalIndex) : next.add(originalIndex);
                              return next;
                            });
                            setOpenActionsMenuIndex(null);
                          }}
                        >
                          {showEvidenceForMsgs.has(originalIndex) ? 'Hide supporting data' : 'View supporting data'}
                        </button>
                      )}
                      {hasExtractionEvidence(originalIndex) && (
                        <button
                          type="button"
                          onClick={() => {
                            setShowExtractionEvidenceForMsgs(prev => {
                              const next = new Set(prev);
                              next.has(originalIndex) ? next.delete(originalIndex) : next.add(originalIndex);
                              return next;
                            });
                            setOpenActionsMenuIndex(null);
                          }}
                        >
                          {showExtractionEvidenceForMsgs.has(originalIndex) ? 'Hide evidence' : 'Show evidence'}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleScheduleReportClick(originalIndex, msg.content)}
                      >
                        Schedule as report
                      </button>
                    </div>
                  ) : null}
                </div>
              )}
              </>)}
              </div>
            </div>
          )}

          {/* Export card attached to the AI message that generated it */}
          {isAI && originalIndex === exportMessageIndex && (() => {
            const format = (activeThread.export_format || 'pdf').toLowerCase();
            const isCsv = format === 'csv';
            const rawName = (activeThread.export_file_name || '').replace(/\.[^.]+$/, '').replace(/[-_]\d{8}_\d{6}$/, '').replace(/[-_]/g, ' ').trim();
            const baseName = rawName || 'ResAI Report';
            // Static: the card names a file that leaves the product. No stored time, no
            // date, rather than this browser's "now".
            const exportedAt = activeThread.export_created_at ? formatTimestamp(activeThread.export_created_at, 'static') : null;
            const reportTitle = exportedAt ? `${baseName} ${exportedAt}` : baseName;
            return (
              <div className="chat-export-inline">
                <div className="chat-export-card">
                  <div className="chat-export-card-icon">
                    {isCsv ? (
                      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#0f2d5c" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></svg>
                    ) : (
                      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#c53030" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><path d="M8 13h8M8 17h4" /></svg>
                    )}
                  </div>
                  <div className="chat-export-card-body">
                    <div className="chat-export-card-title">{reportTitle}</div>
                    <div className="chat-export-card-meta">{isCsv ? 'CSV' : 'PDF'} Report</div>
                  </div>
                  <button
                    type="button"
                    className="chat-export-download-btn"
                    onClick={handleExportDownload}
                    disabled={loading || isStreaming}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 6 }}>
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    DOWNLOAD
                  </button>
                </div>
                <div className="chat-export-success">
                  <span className="thread-export-success-icon" aria-hidden>✓</span>
                  {isCsv ? 'CSV report generated.' : 'PDF report generated.'}
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    );

    // PRD-1571 R10: the owner changed the datasets between the previous turn and
    // this one; new turns use the new ones.
    const ownTurn = sharedThreadOpen && isHuman ? turnsById.get(msg.id) : null;
    // PRD-1644: a query that was ever shared takes the change from /turns, which never names a
    // dataset this viewer can't read; its questions' own dataset names are never used for it.
    const change = !iter1 || !isHuman ? null
      : everShared ? sharedDatasetChange(turnsById.get(msg.id)?.dataset_change)
        : humanDatasetChange(originalIndex);
    const changeLine = change ? (
      <div className="iter1-change" role="note">
        <Icon name={datasetChangeIcon(change)} size={16} className="iter1-change-glyph" />
        <span className="iter1-change-text">{datasetChangeText(change)}</span>
        {msgTime && <span className="iter1-change-time">{msgTime}</span>}
      </div>
    ) : null;
    const stopped = iter1 && isHuman && msg.additional_kwargs?.stopped;
    const datasetsLine = !iter1 && ownTurn && datasetsChangedBefore(ownTurn, turns)
      ? <div className="shared-system-line">{datasetsChangedLine(ownTurn.datasets, datasetNameById)}</div>
      : null;
    // PRD-1571 R8, Frame 2b: the question stays; its answer was given at access
    // this viewer doesn't have, and the server sent none of it.
    const withheld = sharedThreadOpen && isHuman && isWithheld(msg);
    if (!datasetsLine && !withheld && !changeLine && !stopped) return message;
    return (
      <React.Fragment key={`msg-${originalIndex}`}>
        {changeLine}
        {datasetsLine}
        {message}
        {stopped && (
          <StoppedLine onTryAgain={() => handleQuerySubmit(undefined, questionText(msg))} disabled={answerInFlight} />
        )}
        {withheld && iter1 && (
        <div className="message assistant">
          <div className="message-author">
            <img src="/resai-logo.svg" alt="" className="message-avatar message-avatar--logo" aria-hidden="true" />
            <span className="message-author-name">ResAI</span>
            {askerName && <ForChip name={askerName} you={!askedByOther} />}
          </div>
          <WithheldCard icon="prohibit">{WITHHELD_ANSWER_TEXT}</WithheldCard>
        </div>
        )}
        {withheld && !iter1 && (
        <div className="message assistant">
          <div className="message-author">
            <img src="/resai-logo.svg" alt="" className="message-avatar message-avatar--logo" aria-hidden="true" />
            <span className="message-author-name">ResAI</span>
          </div>
          <div className="resui-banner resui-banner--info shared-withheld">
            <Icon name="lock" />
            <span className="resui-banner-text">{WITHHELD_ANSWER}</span>
          </div>
        </div>
        )}
      </React.Fragment>
    );
  };

  const visibleMessages = getVisibleMessages();

  // Empty-conversation "welcome" state: centered input, no docked bottom input.
  // True for a brand-new thread (or none yet) with nothing said. The docked
  // input is the exact inverse so the two never render at once (PRD-1075).
  // PRD-1571 R11 (Frame 3): the open query's datasets are unreadable to this viewer.
  const lockedThreadOpen = sharedQueriesEnabled && activeThread?.locked === true;
  // PRD-1571 Frame 2: an open shared conversation, and whether this viewer is a
  // participant rather than the owner. `shared_at` is never cleared, so it marks
  // threads the server refuses edit/regenerate on (409) even after unsharing.
  const sharedThreadOpen = sharedQueriesEnabled && isThreadShared(activeThread) && !lockedThreadOpen && !sharingEnded;
  const participantView = sharedThreadOpen && !isThreadOwner(activeThread, user);
  const sharedOwnerName = activeThread?.owner_name
    || sharedThreads.find((t) => t.id === activeThread?.id)?.owner_name
    || 'a colleague';
  sharedLiveRef.current = iter1 && sharedThreadOpen;

  // A shared query opens with its pinned datasets selected, not whatever the last query had, so
  // the owner only re-pins what they choose to change and a participant sees them checked. With
  // none of its pins in the list, the selection is left as it is and only recorded, so it isn't
  // re-pinned either.
  useEffect(() => {
    if (!iter1 || !sharedThreadOpen || !loadedDatasets.length) return;
    if (seededPinsRef.current?.threadId === activeThread?.id) return;
    const pinned = activeThread?.pinned_dataset_ids || [];
    const pins = loadedDatasets.filter((d) => pinned.includes(d.id));
    const start = pins.length ? pins : selectedDatasets;
    seededPinsRef.current = { threadId: activeThread.id, ids: start.map((d) => d.id) };
    if (pins.length) setSelectedDatasets(pins);
    // Set up once per opening; later selection changes are the owner's own.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iter1, sharedThreadOpen, activeThread?.id, activeThread?.pinned_dataset_ids, loadedDatasets]);
  // PRD-1581: name each question's asker while shared, and afterwards too if
  // anyone else asked; a conversation only its owner asked in looks as before.
  const askersShown = sharedThreadOpen || othersAsked;

  // PRD-1571 R13: other people's turns appear without a reload. Poll /turns
  // while a shared conversation is open; when a newer turn shows up, reload
  // /state through loadThreadState, which also re-arms the response poll if
  // that turn is still being answered. Skipped while this tab is hidden, while
  // our own answer streams, and while the response poll is already running.
  // A 403/404 means it was unshared or deleted, so stop asking.
  useEffect(() => {
    if (!sharedThreadOpen || !activeThread?.id) return undefined;
    const threadId = activeThread.id;
    let inFlight = false;
    const timer = setInterval(async () => {
      if (inFlight || document.visibilityState === 'hidden') return;
      if (isStreamingRef.current || pendingResponsePollRef.current) return;
      inFlight = true;
      try {
        const list = await apiService.getThreadTurns(threadId);
        if (activeThreadIdRef.current !== threadId) return;
        setTurns(list);
        const newest = maxTurnSeq(list);
        if (newest > lastTurnSeqRef.current) {
          // Shared Queries handoff: say who asked, in the one polite live region. Until the
          // thread's turns have loaded once, nothing is news: older questions aren't announced.
          if (iter1 && turnsPrimedRef.current) {
            list.filter((t) => (t.seq || 0) > lastTurnSeqRef.current && t.author_id !== user?.id)
              .forEach((t) => announce(`${t.author_name || 'Someone'} asked a question in ${activeThread.title || 'this query'}`));
          }
          lastTurnSeqRef.current = newest;
          loadThreadState(threadId);
        }
        turnsPrimedRef.current = true;
      } catch (err) {
        if (err?.status === 403 || err?.status === 404) {
          clearInterval(timer);
          // An owner loses the thread only by losing access to its data.
          const lost = iter1 ? lostAccessReason(err) : null;
          if (iter1 ? lost && (participantView || lost === 'locked') : participantView) handleLostAccess(threadId, err);
        }
      } finally {
        inFlight = false;
      }
    }, TURNS_POLL_MS);
    return () => clearInterval(timer);
    // loadThreadState is recreated every render; the poll only needs the thread.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedThreadOpen, activeThread?.id]);
  const everShared = sharedQueriesEnabled && Boolean(activeThread?.shared_at);

  // Shared Queries handoff 2: who asked the question whose answer failed. /turns names them.
  // Until it does, the question is this viewer's only if this tab just sent it here; otherwise
  // the asker is unknown, so nobody is offered Try again on someone else's question. Once
  // sharing has stopped, only the owner can ask, so an unnamed question is theirs.
  const failedAsker = (() => {
    const index = messages.map((m) => m?.type).lastIndexOf('human');
    const last = index >= 0 ? messages[index] : null;
    const turn = last ? turnsById.get(last.id) : null;
    const you = user?.profile?.display_name || user?.name || 'You';
    if (turn) {
      const mine = turn.author_id === user?.id;
      return { known: true, mine, name: mine ? you : (turn.author_name || 'someone') };
    }
    const sent = lastSentRef.current;
    const sentHere = Boolean(last && sent?.threadId === activeThread?.id
      && questionText(last).trim() === (sent.text || '').trim());
    if (sentHere || !sharedThreadOpen) return { known: true, mine: true, name: you };
    return { known: false, mine: false, name: null };
  })();

  // A failed answer that arrives while the query is open is announced, once; one already there
  // when it opens is not.
  const prevOutcomeRef = useRef(null);
  useEffect(() => {
    const was = prevOutcomeRef.current;
    prevOutcomeRef.current = turnOutcome;
    if (!turnOutcome || was || !iter1 || !askersShown) return;
    const prev = answeredRef.current;
    if (!prev.primed || prev.threadId !== activeThread?.id) return;
    const query = activeThread?.title || 'this query';
    announce(failedAsker.mine
      ? 'Your answer couldn\u2019t be generated'
      : `ResAI couldn\u2019t answer ${failedAsker.name || 'a question'} in ${query}`);
    // Only a new outcome is news.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turnOutcome]);

  // PRD-1643: when a question was asked, for placing share events: its own time, else its turn's.
  const questionTime = (msg) => Date.parse(sentAt(msg) || turnsById.get(msg.id)?.created_at || '');

  // PRD-1645: other askers' photos, fetched once per person per opening (with the auth header,
  // through object URLs) and released when the query changes. Yours is the one already loaded.
  const [askerPhotos, setAskerPhotos] = useState({});
  const askerPhotosRef = useRef({});
  const askerPhotoRequestsRef = useRef(new Set());
  useEffect(() => { askerPhotosRef.current = askerPhotos; }, [askerPhotos]);
  useEffect(() => {
    if (!iter1) return;
    const threadId = activeThread?.id;
    turns.forEach((t) => {
      if (!t.author_photo_url || t.author_id === user?.id || askerPhotoRequestsRef.current.has(t.author_id)) return;
      askerPhotoRequestsRef.current.add(t.author_id);
      apiService.fetchAskerPhotoObjectUrl(t.author_photo_url).then((url) => {
        if (!url) return;
        if (activeThreadIdRef.current !== threadId) {
          URL.revokeObjectURL(url);
          return;
        }
        setAskerPhotos((prev) => ({ ...prev, [t.author_id]: url }));
      });
    });
    // A new list of turns is what can name a new asker.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turns]);
  useEffect(() => () => {
    Object.values(askerPhotosRef.current).forEach((url) => URL.revokeObjectURL(url));
    askerPhotosRef.current = {};
    askerPhotoRequestsRef.current = new Set();
    setAskerPhotos({});
  }, [activeThread?.id]);

  // A new thread starts at its end, with no New activity waiting.
  useEffect(() => {
    nearEndRef.current = true;
    setNewActivity(false);
  }, [activeThread?.id]);

  // Shared Queries handoff (R13): "ResAI answered {name} in {query}" when someone else's
  // question gets its answer while the query is open. Questions already answered when it
  // opened aren't announced.
  useEffect(() => {
    const ids = new Set();
    messages.forEach((m, i) => {
      if (m.type !== 'human') return;
      const next = messages.slice(i + 1).find((n) => n.type === 'human' || n.type === 'ai');
      if (next?.type === 'ai' && next.content) ids.add(m.id);
    });
    const prev = answeredRef.current;
    const sameThread = prev.threadId === activeThread?.id;
    if (sharedLiveRef.current && sameThread && prev.primed) {
      ids.forEach((id) => {
        const turn = turnsById.get(id);
        if (!prev.ids.has(id) && turn && turn.author_id !== user?.id) {
          announce(`ResAI answered ${turn.author_name || 'someone'} in ${activeThread?.title || 'this query'}`);
        }
      });
    }
    // The first messages a thread loads with are its history, not news.
    answeredRef.current = { threadId: activeThread?.id, primed: sameThread && (prev.primed || messages.length > 0), ids };
    // Only a change in the messages can answer a question.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);
  const readOnlyRole = sharedThreadOpen && features?.can_use_ai === false;

  const showWelcome =
    !lockedThreadOpen &&
    !sharingEnded &&
    visibleMessages.length === 0 &&
    !streamingContent &&
    (!activeThread || activeThread.title === 'New conversation');

  /**
   * Find the originalIndex of the AI message that should show the export card.
   * Uses tool messages to find the export_result tool (has blob_path),
   * then finds the next AI message after it — the export confirmation.
   */
  const exportMessageIndex = (() => {
    if (!activeThread?.export_blob_path) return null;

    // Find the last tool message with blob_path in the full messages array
    let exportToolIdx = -1;
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m?.type !== 'tool' || !m.content) continue;
      try {
        const raw = typeof m.content === 'string' ? m.content : JSON.stringify(m.content);
        if (raw.includes('blob_path')) { exportToolIdx = i; break; }
      } catch (_) {}
    }

    if (exportToolIdx >= 0) {
      for (let i = exportToolIdx + 1; i < messages.length; i++) {
        if (messages[i]?.type === 'ai') {
          if (visibleMessages.some(v => v.originalIndex === i)) return i;
          break;
        }
      }
    }

    // Fallback: last visible AI message
    for (let i = visibleMessages.length - 1; i >= 0; i--) {
      if (visibleMessages[i].msg.type === 'ai') return visibleMessages[i].originalIndex;
    }
    return null;
  })();

  return (
    <div
      ref={iter1RootRef}
      className={`chat-container ${isDraggingFile ? 'chat-container--dragging' : ''}${iter1 ? ' chat-container--iter1' : ''}`}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {confirmDeleteDialog}
      {iter1 && announceRegion}
      {iter1 && <TooltipLayer scopeRef={iter1RootRef} />}
      {iter1 && renameTarget && (
        <Modal
          title="Rename query"
          onClose={() => setRenameTarget(null)}
          actions={(
            <>
              <button type="button" className="resui-btn resui-btn--secondary" onClick={() => setRenameTarget(null)}>Cancel</button>
              <button type="submit" form="iter1-rename-form" className="resui-btn resui-btn--primary" disabled={Boolean(renameError)}>Save</button>
            </>
          )}
        >
          <form id="iter1-rename-form" onSubmit={submitRename}>
            <label className="iter1-field-label" htmlFor="iter1-rename-input">Name</label>
            <input
              id="iter1-rename-input"
              className="resui-input"
              value={renameDraft}
              maxLength={240}
              aria-invalid={Boolean(renameError)}
              aria-describedby={renameError ? 'iter1-rename-error' : undefined}
              onChange={(e) => setRenameDraft(e.target.value)}
            />
            {renameError && <p id="iter1-rename-error" className="iter1-field-error">{renameError}</p>}
          </form>
        </Modal>
      )}
      {iter1 && deleteTarget && (
        <Modal
          title={`Delete "${deleteTarget.thread.title || 'this query'}"?`}
          onClose={() => setDeleteTarget(null)}
          initialFocusRef={deleteCancelRef}
          actions={(
            <>
              <button ref={deleteCancelRef} type="button" className="resui-btn resui-btn--secondary" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button type="button" className="resui-btn resui-btn--destructive" onClick={confirmDeleteTarget}>Delete</button>
            </>
          )}
        >
          <p>{deleteTarget.message}</p>
        </Modal>
      )}
      {iter1 && shareTarget && (
        <ShareModal {...shareTarget} onConfirm={confirmShare} onClose={() => setShareTarget(null)} />
      )}
      {iter1 && stopShareTarget && (
        <StopSharingModal {...stopShareTarget} onConfirm={confirmStopSharing} onClose={() => setStopShareTarget(null)} />
      )}
      {sharedQueriesEnabled && sharePopover && (
        <SharePopover
          thread={sharePopover.thread}
          anchorEl={sharePopover.anchorEl}
          onShare={handleShareThread}
          onStopSharing={handleStopSharing}
          onClose={({ restoreFocus }) => (restoreFocus
            ? closeSharePopover(sharePopover.thread)
            : setSharePopover(null))}
        />
      )}
      {/* Sidebar */}
      {iter1 ? (
        <HistoryPanel
          threads={threads}
          sharedThreads={sharedQueriesEnabled ? sharedThreads : []}
          activeThreadId={activeThread?.id}
          loading={loadingThreads}
          isOwner={(t) => isThreadOwner(t, user)}
          sharingEnabled={sharedQueriesEnabled}
          rowRef={(id, el) => { threadRowRefs.current[id] = el; }}
          onSelect={handleSelectThread}
          onTogglePin={handleTogglePin}
          onRename={openRename}
          onShare={sharedQueriesEnabled ? openShare : undefined}
          onStopSharing={sharedQueriesEnabled ? openStopSharing : undefined}
          onDelete={openDelete}
          tab={sideTab}
          onTabChange={setSideTab}
          datasetsTab={(
            <AvailableDatasets
              variant="tab"
              selectedDatasets={selectedDatasets}
              onSelectionChange={setSelectedDatasets}
              onDatasetsLoaded={setLoadedDatasets}
              preselectIds={threadDatasetIds}
              // Shared Queries handoff: locked for participants only; the owner keeps the selector.
              lockedIds={participantView ? (activeThread.pinned_dataset_ids || threadDatasetIds) : null}
              lockedOwner={participantView ? sharedOwnerName : null}
              refreshKey={datasetsRefreshKey}
              enableCsvHitlReview={enableCsvHitlReview}
              submittedReviewJobs={submittedReviewJobs}
              onOpenReview={onOpenReview}
              onViewTranscript={voiceCaptureEnabled ? handleViewTranscript : undefined}
              onRetryDataset={voiceCaptureEnabled ? handleRetryDataset : undefined}
            />
          )}
        />
      ) : (
      <div className={`chat-sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        {sidebarOpen && (
          <>
            <div className="sidebar-nav">
              <button className="sidebar-nav-item" onClick={handleNewConversation}>
                <Icon name="add_circle_outline" />
                New
              </button>
              <button className="sidebar-nav-item" onClick={() => { setSearchQuery(''); setShowSearchModal(true); }}>
                <Icon name="search" />
                Search queries
              </button>
              <button
                className="sidebar-nav-item sidebar-saved-header"
                onClick={() => setSavedQueriesExpanded(prev => !prev)}
              >
                <Icon name="bookmark_border" />
                <span className="sidebar-nav-label">Saved queries</span>
                <Icon name="expand_more" size={18} className={`sidebar-expand-icon ${savedQueriesExpanded ? 'expanded' : ''}`} />
              </button>
            </div>

            {(savedQueriesExpanded || (sharedQueriesEnabled && sharedThreads.length > 0)) && (
              <div className="thread-list">
                {!savedQueriesExpanded ? null : loadingThreads ? (
                  <div className="thread-loading">Loading...</div>
                ) : threads.length === 0 ? (
                  <div className="thread-empty">No saved conversations yet</div>
                ) : (
                  <div className="thread-items-container">
                    {threads.map((thread) => (
                      <div
                        key={thread.id}
                        ref={(el) => { threadRowRefs.current[thread.id] = el; }}
                        className={`thread-item ${activeThread?.id === thread.id ? 'active' : ''}`}
                        onClick={() => renamingThreadId === thread.id ? undefined : handleSelectThread(thread)}
                        role="button"
                        tabIndex={0}
                      >
                        <div className="thread-item-content">
                          {renamingThreadId === thread.id ? (
                            <input
                              className="thread-rename-input"
                              value={renameValue}
                              autoFocus
                              placeholder="Conversation name"
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => setRenameValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') { e.preventDefault(); handleConfirmRename(thread.id); }
                                else if (e.key === 'Escape') { e.preventDefault(); handleCancelRename(); }
                              }}
                              onBlur={() => handleConfirmRename(thread.id)}
                            />
                          ) : (
                            <span className="thread-title">{thread.title || 'Untitled conversation'}</span>
                          )}
                        </div>
                        {renamingThreadId !== thread.id && sharedQueriesEnabled && (
                          <>
                            {thread.locked ? (
                              <Icon name="lock" size={16} className="thread-shared-glyph" title="You don't have access to this query's datasets" />
                            ) : isThreadShared(thread) && (
                              <Icon name="groups" size={16} className="thread-shared-glyph" title="Shared with everyone in your organization" />
                            )}
                            <ThreadRowMenu
                              thread={thread}
                              isOwner={isThreadOwner(thread, user)}
                              onEdit={handleStartRename}
                              // Report conversations can't be shared (the server answers 409).
                              onShare={thread.report_automation_name ? undefined : handleOpenShare}
                              onDelete={(t) => handleDeleteThread(t.id)}
                            />
                          </>
                        )}
                        {renamingThreadId !== thread.id && !sharedQueriesEnabled && (
                          <>
                            <button
                              className="thread-rename"
                              onClick={(e) => handleStartRename(thread, e)}
                              title="Rename"
                            >
                              <Icon name="edit" />
                            </button>
                            <button
                              className="thread-delete"
                              onClick={(e) => handleDeleteThread(thread.id, e)}
                              title="Delete"
                            >
                              ×
                            </button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {sharedQueriesEnabled && (
                  <SharedWithMeList
                    threads={sharedThreads}
                    activeThreadId={activeThread?.id}
                    expanded={sharedExpanded}
                    onToggle={() => setSharedExpanded(prev => !prev)}
                    onSelect={handleSelectThread}
                  />
                )}
              </div>
            )}
          </>
        )}
      </div>
      )}

      {/* Main Chat Area */}
      <div className="chat-main">
        {/* Dataset required banner */}
        {!iter1 && selectedDatasets.length === 0 && !lockedThreadOpen && !sharingEnded && (
          <div className="resui-banner resui-banner--info chat-dataset-banner">
            <Icon name="info" />
            <span className="resui-banner-text">Select at least one dataset to start a conversation.</span>
          </div>
        )}

        {/* Error Banner */}
        {error && (
          <div className="chat-error">
            {error}
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        {iter1 && activeThread && sharingEnded && (
          <ThreadHeader
            thread={goneReason === 'locked' && !isThreadOwner(activeThread, user)
              ? { ...activeThread, title: `Shared query from ${sharedOwnerName}` }
              : activeThread}
            datasetNames={[]}
            onNewQuery={handleNewConversation}
            menuProps={goneReason === 'locked' && isThreadOwner(activeThread, user) ? {
              isOwner: true,
              sharingEnabled: sharedQueriesEnabled,
              onTogglePin: handleTogglePin,
              onRename: openRename,
              onShare: openShare,
              onStopSharing: openStopSharing,
              onDelete: openDelete,
            } : undefined}
          />
        )}
        {iter1 && activeThread && !showWelcome && !lockedThreadOpen && !sharingEnded && (
          <ThreadHeader
            thread={activeThread}
            datasetNames={(threadDatasetIds.length
              ? threadDatasetIds.map((id) => datasetNameById.get(id)).filter(Boolean)
              : selectedDatasets.map((d) => d.name))}
            onNewQuery={handleNewConversation}
            scrolled={threadScrolled}
            meta={sharedThreadOpen ? (
              <SharedLine
                people={askersFrom(turns).map((a) => (a.id === user?.id
                  ? { ...a, photoUrl: userAvatarKind === 'photo' ? userAvatarUrl : null, color: userAvatarColor }
                  : { ...a, photoUrl: askerPhotos[a.id] || null }))}
                ownerLabel={participantView ? sharedOwnerName : 'you'}
              />
            ) : null}
            menuProps={{
              isOwner: isThreadOwner(activeThread, user),
              sharingEnabled: sharedQueriesEnabled,
              onTogglePin: handleTogglePin,
              onRename: openRename,
              onShare: sharedQueriesEnabled ? openShare : undefined,
              onStopSharing: sharedQueriesEnabled ? openStopSharing : undefined,
              onDelete: openDelete,
            }}
          />
        )}
        {sharedThreadOpen && !iter1 && (
          <SharedConversationHeader
            thread={activeThread}
            ownerName={isThreadOwner(activeThread, user)
              ? (user?.profile?.display_name || user?.name || 'You')
              : (activeThread.owner_name
                || sharedThreads.find((t) => t.id === activeThread.id)?.owner_name
                || 'a colleague')}
            turns={turns}
          />
        )}

        {/* Messages Area */}
        <div className="chat-messages" onScroll={iter1 ? handleMessagesScroll : undefined}>
          {sharingEnded && iter1 ? (
            <WithheldCard icon="prohibit" className="iter1-thread-gone">{QUERY_GONE[goneReason || 'unshared']}</WithheldCard>
          ) : sharingEnded ? (
            <SharingEndedNotice />
          ) : lockedThreadOpen ? (
            <LockedQueryNotice thread={activeThread} />
          ) : showWelcome ? (
            <div className="chat-welcome">
              <h3 className="chat-welcome-heading">What should the data help you decide?</h3>
              <div className="chat-input-centered">
                <form className="chat-form" onSubmit={handleQuerySubmit}>
                  {!iter1 && selectedDatasets.length > 0 && (
                    <div className="selected-datasets">
                      <span className="dataset-label">Using:</span>
                      {selectedDatasets.map((dataset) => (
                        <Chip
                          key={dataset.id}
                          className="dataset-tag"
                          onDismiss={() => setSelectedDatasets(prev => prev.filter(d => d.id !== dataset.id))}
                        >
                          {dataset.name}
                        </Chip>
                      ))}
                    </div>
                  )}
                  <div className="chat-input-wrapper chat-input-wrapper--welcome">
                    <textarea
                      ref={textareaRef}
                      className="chat-input chat-input--top"
                      placeholder={iter1 && selectedDatasets.length === 0
                        ? 'Select or upload a dataset to ask a question'
                        : 'Ask a question about the selected datasets'}
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      onKeyDown={handleKeyDown}
                      disabled={loading && !isStreaming}
                      rows={1}
                    />
                    <div className="chat-input-toolbar">
                      <div className="chat-input-toolbar-left">
                        {!iter1 && (
                          <OutputFormatSelect
                            formats={availableFormats}
                            value={selectedOutputFormat}
                            onChange={setSelectedOutputFormat}
                          />
                        )}
                      </div>
                      <button
                        type="submit"
                        className="send-btn send-btn--circle"
                        disabled={answerInFlight || (!query.trim() && selectedFiles.length === 0) || selectedDatasets.length === 0}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="12" y1="19" x2="12" y2="5" />
                          <polyline points="5 12 12 5 19 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          ) : (
            <>
              {/* PRD-1643: a query that was ever shared shows where it was shared, stopped and
                  shared again, placed by time among its questions. */}
              {(iter1 && everShared
                ? withShareEvents(visibleMessages, activeThread?.share_events, questionTime)
                : visibleMessages
              ).map((item) => (item.shareEvent ? (
                <ShareEventDivider
                  key={`share-${item.shareEvent.event}-${item.shareEvent.at}`}
                  event={item.shareEvent.event}
                  at={item.shareEvent.at}
                />
              ) : renderMessage(item.msg, item.originalIndex)))}

              {/* Up for the whole load, not only while the list is empty, and pinned to
                  the bottom of the pane once messages show: a long thread used to sit
                  looking finished with no sign it was still loading. */}
              {threadLoading && (
                <div
                  className={`chat-thread-loading${visibleMessages.length ? ' chat-thread-loading--pinned' : ''}`}
                  role="status"
                >
                  <div className="thinking-dots">
                    <span></span><span></span><span></span>
                  </div>
                  <span className="thinking-label">Loading conversation</span>
                </div>
              )}

              {answerInFlight && iter1 && <ProgressRow startedAt={runStartedAt} announce={announce} />}
              {answerInFlight && !iter1 && (
                <div className="message assistant thinking">
                  <div className="message-content">
                    <div className="thinking-message">
                      <div className="thinking-dots">
                        <span></span><span></span><span></span>
                      </div>
                      <span className="thinking-label">Thinking</span>
                    </div>
                  </div>
                </div>
              )}
              {iter1 && turnOutcome && !answerInFlight && (askersShown ? (
                <SharedFailedAnswer
                  outcome={turnOutcome}
                  askerKnown={failedAsker.known}
                  askerName={failedAsker.name}
                  mine={failedAsker.mine}
                  automateNote={participantView ? `Only ${sharedOwnerName} can automate this query` : null}
                  onTryAgain={retryLastQuestion}
                  onCopy={handleCopyContent}
                  disabled={answerInFlight}
                />
              ) : (
                <FailedAnswer outcome={turnOutcome} onTryAgain={retryLastQuestion} disabled={answerInFlight} />
              ))}

              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input Area - docked input for an active conversation. Hidden while the
            centered welcome input is showing so the two never both render. */}
        {readOnlyRole && (
          <div className="chat-input-area">
            {iter1 && newActivity && <NewActivityButton onClick={jumpToNewActivity} />}
            {iter1 ? (
              <WithheldCard icon="eye" className="iter1-composer-readonly">{READ_ONLY_TEXT}</WithheldCard>
            ) : (
            <div className="resui-banner resui-banner--info">
              <Icon name="info" />
              <span className="resui-banner-text">{READ_ONLY_ROLE}</span>
            </div>
            )}
          </div>
        )}
        {!showWelcome && !lockedThreadOpen && !sharingEnded && !readOnlyRole && (
        <div className="chat-input-area">
          {iter1 && newActivity && <NewActivityButton onClick={jumpToNewActivity} />}
          <form className="chat-form" onSubmit={handleQuerySubmit}>
            {!iter1 && selectedDatasets.length > 0 && (
              <div className="selected-datasets">
                <span className="dataset-label">Using:</span>
                {selectedDatasets.map((dataset) => (
                  <Chip
                    key={dataset.id}
                    className="dataset-tag"
                    onDismiss={sharedThreadOpen
                      ? undefined
                      : () => setSelectedDatasets(prev => prev.filter(d => d.id !== dataset.id))}
                  >
                    {dataset.name}
                  </Chip>
                ))}
              </div>
            )}
            {selectedFiles.length > 0 && (
              <div className="selected-files">
                {selectedFiles.map((file, index) => (
                  <Chip key={index} className="selected-file" onDismiss={() => removeSelectedFile(index)}>
                    {file.name}
                  </Chip>
                ))}
              </div>
            )}
            <div className="chat-input-wrapper chat-input-wrapper--welcome">
              <textarea
                ref={textareaRef}
                className="chat-input chat-input--top"
                placeholder={iter1 ? COMPOSER_PLACEHOLDER : (sharedThreadOpen ? SHARED_COMPOSER_PLACEHOLDER : 'Ask a question about the selected datasets')}
                aria-describedby={iter1 && sharedThreadOpen ? 'iter1-audience-note' : undefined}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={loading && !isStreaming}
                rows={1}
              />
              <div className="chat-input-toolbar">
                <div className="chat-input-toolbar-left">
                  {!iter1 && (
                    <OutputFormatSelect
                      formats={availableFormats}
                      value={selectedOutputFormat}
                      onChange={setSelectedOutputFormat}
                    />
                  )}
                  {sharedThreadOpen && !iter1 && (
                    <span className="shared-audience">
                      <Icon name="groups" size={16} /> {SHARED_AUDIENCE}
                    </span>
                  )}
                </div>
              {iter1 && answerInFlight && (isStreaming || ownRun) ? (
                <button type="button" className="send-btn send-btn--circle iter1-stop" onClick={handleStop} aria-label="Stop" data-tip="Stop">
                  <Icon name="stop" size={18} weight="fill" />
                </button>
              ) : (
              <button
                type="submit"
                className="send-btn send-btn--circle"
                aria-label="Send"
                disabled={answerInFlight || (!query.trim() && selectedFiles.length === 0) || selectedDatasets.length === 0}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="19" x2="12" y2="5" />
                  <polyline points="5 12 12 5 19 12" />
                </svg>
              </button>
              )}
              </div>
            </div>
            {iter1 && sharedThreadOpen && <AudienceNote id="iter1-audience-note" />}
            {answerInFlight && !iter1 && <p className="chat-input-hint" role="status">{STILL_ANSWERING_HINT}</p>}
          </form>
        </div>
        )}

        {/* SOP Library footer — sticky-per-user selection of module-owned
            datasets that ground the next query alongside the working
            datasets in the right sidebar. Gated by `enable_module_framework`
            so the module-framework UI can be rolled out per environment. */}
        {moduleFrameworkEnabled && !iter1 && (
          <ModuleLibraryFooter
            onShowNotification={onShowNotification}
            onSelectionChange={setModuleSelectedDatasetIds}
          />
        )}
      </div>

      {/* Dataset Panel (Right) — Iteration 1 moves datasets into the side panel's tab. */}
      {!iter1 && (
      <div className={`dataset-panel ${datasetPanelOpen ? 'open' : 'closed'}`}>
        {datasetPanelOpen && (
          <AvailableDatasets
            selectedDatasets={selectedDatasets}
            onSelectionChange={setSelectedDatasets}
            onDatasetsLoaded={setLoadedDatasets}
            preselectIds={threadDatasetIds}
            // The server answers every turn of a shared query over its pinned
            // datasets, the owner's included, so the panel is locked for everyone
            // until owner re-pinning (PATCH dataset_ids) is designed.
            lockedIds={sharedThreadOpen ? (activeThread.pinned_dataset_ids || threadDatasetIds) : null}
            refreshKey={datasetsRefreshKey}
            enableCsvHitlReview={enableCsvHitlReview}
            submittedReviewJobs={submittedReviewJobs}
            onOpenReview={onOpenReview}
            onAddClick={quickUploadEnabled ? handleQuickUploadClick : undefined}
            onRecordClick={voiceCaptureEnabled ? handleRecordClick : undefined}
            onViewTranscript={voiceCaptureEnabled ? handleViewTranscript : undefined}
            onRetryDataset={voiceCaptureEnabled ? handleRetryDataset : undefined}
            recorderSlot={voiceCaptureEnabled && showVoiceRecorder ? (
              <VoiceRecorderWidget
                onClose={handleVoiceRecorderClose}
                onShowNotification={onShowNotification}
                onSubmitted={handleVoiceSubmitted}
              />
            ) : null}
          />
        )}
      </div>
      )}

      {/* Search Queries Modal */}
      {showSearchModal && (
        <div className="search-modal-overlay" onClick={() => setShowSearchModal(false)}>
          <div className="search-modal" onClick={(e) => e.stopPropagation()}>
            <div className="search-modal-header">
              <h3>Search queries</h3>
              <button className="search-modal-close" onClick={() => setShowSearchModal(false)}>×</button>
            </div>
            <input
              className="search-modal-input"
              placeholder="Search saved queries..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
            />
            <div className="search-modal-results">
              {threads
                .filter((t) => !searchQuery || (t.title || '').toLowerCase().includes(searchQuery.toLowerCase()))
                .map((thread) => (
                  <div
                    key={thread.id}
                    className="search-modal-item"
                    onClick={() => { handleSelectThread(thread); setShowSearchModal(false); }}
                  >
                    {thread.title || 'Untitled conversation'}
                  </div>
                ))}
              {threads.filter((t) => !searchQuery || (t.title || '').toLowerCase().includes(searchQuery.toLowerCase())).length === 0 && (
                <div className="search-modal-empty">No matching queries found</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Schema Approval Modal */}
      {showApprovalModal && pendingApproval && (
        <SchemaApprovalModal
          schema={pendingApproval.schema}
          fields={pendingApproval.fields}
          message={pendingApproval.message}
          onApprove={() => handleApproval('approve')}
          onReject={(feedback) => handleApproval('reject', feedback)}
          onClose={() => setShowApprovalModal(false)}
        />
      )}

      {/* Quick-upload paperclip flow: hidden picker + modal */}
      {quickUploadEnabled && (
        <>
          <input
            ref={quickUploadInputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={handleQuickUploadFilesChosen}
          />
          <QuickUploadModal
            isOpen={showQuickUpload}
            initialFiles={quickUploadFiles}
            onClose={handleQuickUploadClose}
            onShowNotification={onShowNotification}
            onPublishSuccess={handleQuickUploadPublishSuccess}
          />
        </>
      )}

      {/* In-chat voice recording (PRD-941): the recorder renders inline inside the
          dataset panel (see AvailableDatasets recorderSlot); only the transcript viewer
          remains a modal. */}
      {voiceCaptureEnabled && transcriptDataset && (
        <TranscriptViewerModal
          isOpen={!!transcriptDataset}
          datasetId={transcriptDataset.id}
          datasetName={transcriptDataset.name}
          onClose={() => setTranscriptDataset(null)}
          onShowNotification={onShowNotification}
        />
      )}

    </div>
  );
};

export default AskResAI;
